import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, doc, getDoc, updateDoc, setDoc, deleteDoc, onSnapshot, addDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { auth } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminArtistManager from '@/components/AdminArtistManager';
import AdminFinancialPanel from '@/components/AdminFinancialPanel';
import AdminNotificationManager from '@/features/admin/components/AdminNotificationManager';
import AdminReferralConsole from '@/features/admin/components/AdminReferralConsole';
import ContactSubmissionsManager from '@/features/admin/components/ContactSubmissionsManager';
import AdminStoreManager from '@/features/admin/components/AdminStoreManager';
import AdminQrRedirectsPanel from '@/features/admin/components/AdminQrRedirectsPanel';
import AdminCollapsibleSection from '@/features/admin/components/AdminCollapsibleSection';
import { useToast } from '@/hooks/useToast';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { isAdminAnalyticsPaidRequest } from '@/lib/payoutLedger';
import { 
  Users, 
  UserPlus, 
  Music, 
  Calendar, 
  MapPin,
  DollarSign, 
  TrendingUp, 
  Search, 
  Edit, 
  Trash2,
  Plus,
  Eye,
  LogOut,
  Shield,
  BarChart3,
  Globe,
  MessageCircle,
  AlertCircle,
  Gift,
  Mail,
  Radio,
  Filter,
  RefreshCw,
  QrCode,
  ExternalLink,
  Download,
  Target,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  Wallet,
  CreditCard,
  Mic,
  Briefcase,
  X,
  LogIn,
  Sparkles
} from 'lucide-react';
import QRCode from 'qrcode.react';
import { buildArtistPublicUrl } from '@/lib/urlHelpers';
import { downloadQrPngHighRes, getQrPngDataUrlHighRes, QR_HIGH_RES_PX } from '@/lib/qrHighRes';
import { getFunctions } from 'firebase/functions';
import { COUNTRIES, getCountryName } from '@/lib/countries';
import { getCurrencyForCountry } from '@/lib/currency';
import { formatUkDate, formatUkDateTime } from '@/lib/adminFormat';
import { LiveSessionsView } from '@/features/admin/components/AdminLiveSessionsView';
import { AllSessionsView } from '@/features/admin/components/AdminAllSessionsView';
import { ArtistDetailsModal } from '@/features/admin/components/AdminArtistDetailsModal';
import { AdminClockIcon } from '@/features/admin/components/AdminModalIcons';
import { STRIPE_LEGACY_PROFILE_KEYS } from '@/lib/stripeProfilePurge';
import { ARTIST_ANALYTICS_EVENT_TYPES } from '@/lib/recordArtistAnalyticsEvent';
import AdminMfaEnrollment from '@/components/admin/AdminMfaEnrollment';
import AdminMfaChallenge from '@/components/admin/AdminMfaChallenge';

const AdminConsolePage = () => {
  const {
    isAdminAuthenticated,
    adminSignOut,
    isCheckingSession,
    needsMfaEnrollment,
    needsMfaChallenge,
    refreshMfaState,
  } = useAdminAuth();
  // Admin identity alone is not enough to load anything once 2FA is enrolled.
  const mfaSatisfied = !needsMfaEnrollment && !needsMfaChallenge;
  const adminReady = isAdminAuthenticated && mfaSatisfied;
  const navigate = useNavigate();
  const { toast } = useToast();
  const mobile = useMobile();
  const { format } = useCurrency();
  
  console.log('🔍 AdminConsolePage render:', { isAdminAuthenticated });
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalArtists: 0,
    newToday: 0,
    newThisWeek: 0,
    newThisMonth: 0,
    totalSongs: 0,
    totalGigs: 0,
    totalEarnings: 0,
    averageSongPrice: 0,
    medianSongPrice: 0,
    mostPopularPrice: 0,
    totalPurchases: 0,
    averageTimeToFirstLive: 0,
    totalReferrals: 0,
    averageARPU: 0,
    averageLTV: 0
  });
  
  const [artists, setArtists] = useState([]);
  const [filteredArtists, setFilteredArtists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'name', 'date', 'email'
  const [sortOrder, setSortOrder] = useState('desc'); // default: Date Signed Up newest first (Z→A)
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [showArtistDetails, setShowArtistDetails] = useState(false);
  const [showArtistManager, setShowArtistManager] = useState(false);
  const [showAgentManager, setShowAgentManager] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [communicationSection, setCommunicationSection] = useState(null);
  
  // New state for live status filtering and artist stats
  const [liveStatusFilter, setLiveStatusFilter] = useState('all'); // 'all', 'live', 'never_live', 'offline'
  /** 'all' | 'artist' | 'karaoke' | 'agent' — composable with live status filter */
  const [accountTypeFilter, setAccountTypeFilter] = useState('all');
  const [artistLiveStats, setArtistLiveStats] = useState({}); // { artistId: { isCurrentlyLive, hasEverGoneLive, totalSessions, lastLiveDate } }
  const [loadingLiveStats, setLoadingLiveStats] = useState(true);
  /** Per-artist aggregates for Analytics → Quick Stats (cohort-based averages; excludes dormant signups). */
  const [quickStatsAgg, setQuickStatsAgg] = useState(null);
  const [hoveredArtist, setHoveredArtist] = useState(null); // Track which artist's badge is being hovered
  const [analyticsData, setAnalyticsData] = useState({
    dailyStats: [],
    topArtists: [],
    recentActivity: [],
    revenueTrends: [],
    userGrowth: [],
    webLogins: [],
    trialDemoCompletions: [],
  });
  /** Analytics → Web sign-ins / visits panel */
  const [webLoginsSectionOpen, setWebLoginsSectionOpen] = useState(false);
  /** Analytics → Live trial demo completed */
  const [trialDemoSectionOpen, setTrialDemoSectionOpen] = useState(false);
  const [selectedArtists, setSelectedArtists] = useState([]);
  const [bulkOperationMode, setBulkOperationMode] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Competition state
  const [competitionEnabled, setCompetitionEnabled] = useState(false);
  const [competitionPrize, setCompetitionPrize] = useState('Win an all-expenses paid trip to the home of music; Nashville!');
  const [competitionLeaderboard, setCompetitionLeaderboard] = useState([]);
  const [loadingCompetition, setLoadingCompetition] = useState(false);
  const [competitionStart, setCompetitionStart] = useState('');
  const [competitionEnd, setCompetitionEnd] = useState('');
  const [referralSystemEnabled, setReferralSystemEnabled] = useState(true);

  // Helpers for GMT formatting
  const toDisplayGmt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    const HH = String(d.getUTCHours()).padStart(2, '0');
    const MM = String(d.getUTCMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${HH}:${MM}`;
  };

  // Helper to convert ISO to datetime-local format (YYYY-MM-DDTHH:mm)
  const isoToDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const yyyy = String(d.getUTCFullYear());
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const HH = String(d.getUTCHours()).padStart(2, '0');
    const MM = String(d.getUTCMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${HH}:${MM}`;
  };

  // Helper to convert datetime-local to ISO UTC
  const datetimeLocalToIsoUtc = (datetimeLocal) => {
    if (!datetimeLocal) return null;
    try {
      // datetime-local is in local time, so we need to parse it as if it's UTC
      const [datePart, timePart] = datetimeLocal.split('T');
      if (!datePart || !timePart) return null;
      const [yyyy, mm, dd] = datePart.split('-').map(Number);
      const [HH, MM] = timePart.split(':').map(Number);
      const ms = Date.UTC(yyyy, mm - 1, dd, HH, MM, 0);
      if (isNaN(ms)) return null;
      return new Date(ms).toISOString();
    } catch (err) {
      console.error('Date parsing error:', err);
      return null;
    }
  };

  // Load referral system setting
  useEffect(() => {
    const loadReferralSetting = async () => {
      if (!db || !isAdminAuthenticated) return;
      try {
        const snap = await getDoc(doc(db, 'settings', 'referralSystem'));
        if (snap.exists()) {
          setReferralSystemEnabled(snap.data().enabled !== false); // Default to true if not set
        }
      } catch (e) {
        console.error('Failed to load referral system setting', e);
      }
    };
    if (db && isAdminAuthenticated) {
      loadReferralSetting();
    }
  }, [db, isAdminAuthenticated]);

  // Load competition settings
  useEffect(() => {
    const loadCompetition = async () => {
      if (!db || !isAdminAuthenticated) return;
      try {
        const snap = await getDoc(doc(db, 'competitions', 'current'));
        if (snap.exists()) {
          const data = snap.data();
          setCompetitionEnabled(!!data.enabled);
          if (data.prize) setCompetitionPrize(data.prize);
          // Use datetime-local format for the inputs
          setCompetitionStart(data.startAt ? isoToDatetimeLocal(data.startAt) : '');
          setCompetitionEnd(data.endAt ? isoToDatetimeLocal(data.endAt) : '');
        }
      } catch (e) {
        console.error('Failed to load competition settings', e);
        console.error('Error code:', e.code, 'Error message:', e.message);
      }
    };
    if (db && isAdminAuthenticated) {
      loadCompetition();
    }
  }, [db, isAdminAuthenticated]);

  // Load leaderboard when Competition tab active - REAL-TIME
  useEffect(() => {
    if (!db || activeTab !== 'competition') {
      setCompetitionLeaderboard([]);
      return;
    }

    if (!isAdminAuthenticated) {
      console.log('Admin not authenticated, skipping leaderboard subscription');
      return;
    }

    setLoadingCompetition(true);
    
    // Subscribe to real-time updates of competitionPoints collection
    // Filter to only show artists with points > 0
    const leaderboardQuery = query(
      collection(db, 'competitionPoints'),
      where('totalPoints', '>', 0),
      orderBy('totalPoints', 'desc'),
      limit(20)
    );

    console.log('📊 Setting up real-time leaderboard subscription...');

    const unsubscribe = onSnapshot(
      leaderboardQuery,
      async (snapshot) => {
        console.log(`📊 Leaderboard snapshot received: ${snapshot.docs.length} documents`);
        try {
          const enriched = [];
          for (const docSnap of snapshot.docs) {
            try {
              const row = docSnap.data();
              const userId = row.userId || docSnap.id;
              
              // Only include if they have points
              if (!row.totalPoints || row.totalPoints <= 0) {
                continue;
              }
              
              const p = await getDoc(doc(db, 'profiles', userId));
              enriched.push({
                id: userId,
                name: p.exists() ? (p.data().artist_name || p.data().karaoke_name || 'Unknown') : 'Unknown',
                email: p.exists() ? (p.data().email || '') : '',
                points: row.totalPoints || 0
              });
            } catch (err) {
              console.warn('Error processing leaderboard entry:', err);
              const row = docSnap.data();
              if (row.totalPoints && row.totalPoints > 0) {
                enriched.push({ 
                  id: row.userId || docSnap.id, 
                  name: 'Unknown', 
                  email: '', 
                  points: row.totalPoints || 0 
                });
              }
            }
          }
          console.log(`📊 Processed ${enriched.length} leaderboard entries`);
          setCompetitionLeaderboard(enriched);
        } catch (e) {
          console.error('Failed to process leaderboard snapshot', e);
        } finally {
          setLoadingCompetition(false);
        }
      },
      (error) => {
        console.error('Failed to subscribe to leaderboard', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        setLoadingCompetition(false);
        setCompetitionLeaderboard([]);
      }
    );

    return () => {
      console.log('📊 Unsubscribing from leaderboard');
      unsubscribe();
    };
  }, [activeTab, db, isAdminAuthenticated]);

  // Redirect if not authenticated - but wait for session check to complete
  useEffect(() => {
    // Don't redirect if we're still checking the session
    if (isAdminAuthenticated === undefined || isCheckingSession) {
      return;
    }
    
    if (!isAdminAuthenticated) {
      console.log('🔒 Admin not authenticated, redirecting to login');
      navigate('/admin-login');
    }
  }, [isAdminAuthenticated, isCheckingSession, navigate]);

  // Fetch statistics and artist data - optimized with lazy loading
  useEffect(() => {
    if (adminReady) {
      // Load critical data first (artists list)
      fetchAdminData();
      // Defer analytics to avoid blocking initial render
      setTimeout(() => {
        fetchAnalyticsData();
      }, 500);
    }
  }, [adminReady]);

  // Refresh analytics data when Analytics tab is opened
  useEffect(() => {
    if (isAdminAuthenticated && activeTab === 'analytics') {
      fetchAnalyticsData();
    }
  }, [activeTab, isAdminAuthenticated]);

  // Add error handling for Firebase connection issues
  const [firebaseError, setFirebaseError] = useState(null);

  // Function to fetch live status data for artists
  const fetchArtistLiveStats = async (artistIds) => {
    if (!db || artistIds.length === 0) {
      setLoadingLiveStats(false);
      return;
    }

    setLoadingLiveStats(true);
    const stats = {};

    try {
      // Fetch live_status documents for all artists
      for (const artistId of artistIds) {
        try {
          const liveStatusDoc = await getDoc(doc(db, 'live_status', artistId));
          const liveStatusData = liveStatusDoc.exists() ? liveStatusDoc.data() : null;
          
          // Count total sessions from session_history collection (accurate count)
          let totalSessions = 0;
          try {
            const sessionHistoryQuery = query(
              collection(db, 'session_history'),
              where('user_id', '==', artistId)
            );
            const sessionHistorySnapshot = await getDocs(sessionHistoryQuery);
            totalSessions = sessionHistorySnapshot.docs.length;
          } catch (err) {
            // Silently handle permission errors for session_history (expected if admin doesn't have access)
            if (err.code !== 'permission-denied') {
              console.warn('Error fetching session count from session_history:', err);
            }
            // Fallback: try counting unique session_ids from requests if session_history fails
            try {
              const requestsQuery = query(collection(db, 'requests'), where('artist_id', '==', artistId));
              const requestsSnapshot = await getDocs(requestsQuery);
              const uniqueSessionIds = new Set();
              requestsSnapshot.docs.forEach(doc => {
                const sessionId = doc.data().session_id;
                if (sessionId) uniqueSessionIds.add(sessionId);
              });
              totalSessions = uniqueSessionIds.size;
            } catch (fallbackErr) {
              console.warn('Error with fallback session count:', fallbackErr);
            }
          }

          stats[artistId] = {
            isCurrentlyLive: liveStatusData?.is_live || false,
            hasEverGoneLive: liveStatusDoc.exists() || totalSessions > 0,
            totalSessions: totalSessions,
            lastLiveDate: liveStatusData?.updated_at?.toDate?.() || liveStatusData?.session_start_time?.toDate?.() || null
          };
          
          // Debug logging
          if (liveStatusData?.is_live) {
            console.log(`[Live Status] Artist ${artistId} is currently LIVE:`, liveStatusData);
          }
        } catch (err) {
          console.warn(`Error fetching live stats for artist ${artistId}:`, err);
          stats[artistId] = {
            isCurrentlyLive: false,
            hasEverGoneLive: false,
            totalSessions: 0,
            lastLiveDate: null
          };
        }
      }

      setArtistLiveStats(stats);
    } catch (error) {
      console.error('Error fetching artist live stats:', error);
    } finally {
      setLoadingLiveStats(false);
    }
  };

  /**
   * Analytics → Quick Stats: cohort-based averages so dormant signups (no catalogue,
   * no gigs, no live session) do not dilute metrics.
   * - Avg songs: among artists with ≥1 catalogue song (non-archived), including inactive.
   * - Avg gigs: among artists with ≥1 gig.
   * - Avg revenue: among artists with ≥1 live session (session_history / live stats, or any request with session_id).
   */
  const quickStatsDisplay = useMemo(() => {
    const empty = {
      avgSongsPerCatalogueArtist: 0,
      avgGigsPerGigArtist: 0,
      avgRevenuePerLiveArtist: 0,
      catalogueArtistCount: 0,
      gigArtistCount: 0,
      liveSessionArtistCount: 0,
    };
    if (!quickStatsAgg || artists.length === 0) return empty;

    const artistIds = artists.map((a) => a.id);
    const {
      songCountByArtistId,
      gigCountByArtistId,
      revenueByArtistId,
      sessionArtistIdsFromRequests,
    } = quickStatsAgg;
    const sessionHint = new Set(sessionArtistIdsFromRequests || []);

    const hasHadLiveSession = (id) => {
      const ls = artistLiveStats[id];
      if (ls?.totalSessions > 0) return true;
      return sessionHint.has(id);
    };

    const catalogueArtistIds = artistIds.filter(
      (id) => (songCountByArtistId[id] || 0) > 0
    );
    const totalSongsInCohort = catalogueArtistIds.reduce(
      (s, id) => s + (songCountByArtistId[id] || 0),
      0
    );
    const avgSongsPerCatalogueArtist =
      catalogueArtistIds.length > 0
        ? totalSongsInCohort / catalogueArtistIds.length
        : 0;

    const gigArtistIds = artistIds.filter(
      (id) => (gigCountByArtistId[id] || 0) > 0
    );
    const totalGigsInCohort = gigArtistIds.reduce(
      (s, id) => s + (gigCountByArtistId[id] || 0),
      0
    );
    const avgGigsPerGigArtist =
      gigArtistIds.length > 0 ? totalGigsInCohort / gigArtistIds.length : 0;

    const liveArtistIds = artistIds.filter(hasHadLiveSession);
    const revenueSumLiveCohort = liveArtistIds.reduce(
      (s, id) => s + (revenueByArtistId[id] || 0),
      0
    );
    const avgRevenuePerLiveArtist =
      liveArtistIds.length > 0 ? revenueSumLiveCohort / liveArtistIds.length : 0;

    return {
      avgSongsPerCatalogueArtist,
      avgGigsPerGigArtist,
      avgRevenuePerLiveArtist,
      catalogueArtistCount: catalogueArtistIds.length,
      gigArtistCount: gigArtistIds.length,
      liveSessionArtistCount: liveArtistIds.length,
    };
  }, [quickStatsAgg, artists, artistLiveStats]);

  // Filter and sort artists based on search term and sort options
  useEffect(() => {
    let filtered = artists;
    
    // Apply search filter - unified search across all fields
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(artist => {
        // Search by Artist ID (exact match for IDs)
        if (searchTerm.length >= 28 && artist.id === searchTerm) {
          return true;
        }
        
        // Search across multiple fields (case-insensitive partial match)
        return (
          artist.artist_name?.toLowerCase().includes(searchLower) ||
          artist.karaoke_name?.toLowerCase().includes(searchLower) ||
          artist.email?.toLowerCase().includes(searchLower) ||
          artist.url_slug?.toLowerCase().includes(searchLower) ||
          artist.full_name?.toLowerCase().includes(searchLower) ||
          artist.stripe_account_id?.toLowerCase().includes(searchLower) ||
          artist.country?.toLowerCase().includes(searchLower) ||
          artist.currency?.toLowerCase().includes(searchLower) ||
          artist.id?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (accountTypeFilter !== 'all') {
      filtered = filtered.filter((a) => {
        const normalizedType = a.account_type === 'agent'
          ? 'agent'
          : a.account_type === 'karaoke'
            ? 'karaoke'
            : 'artist';
        return normalizedType === accountTypeFilter;
      });
    }

    // Apply live status filter
    if (liveStatusFilter !== 'all') {
      filtered = filtered.filter(artist => {
        const stats = artistLiveStats[artist.id];
        if (!stats) return false;
        
        switch (liveStatusFilter) {
          case 'live':
            return stats.isCurrentlyLive;
          case 'never_live':
            return !stats.hasEverGoneLive;
          case 'offline':
            return stats.hasEverGoneLive && !stats.isCurrentlyLive;
          default:
            return true;
        }
      });
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.artist_name?.toLowerCase() || '';
          bValue = b.artist_name?.toLowerCase() || '';
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'date':
          aValue = new Date(a.created_at || a.createdAt || 0);
          bValue = new Date(b.created_at || b.createdAt || 0);
          break;
        default:
          aValue = a.artist_name?.toLowerCase() || '';
          bValue = b.artist_name?.toLowerCase() || '';
      }
      
      if (sortBy === 'date') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
    
    setFilteredArtists(filtered);
  }, [searchTerm, artists, sortBy, sortOrder, liveStatusFilter, accountTypeFilter, artistLiveStats]);

  const fetchAdminData = async () => {
    setLoading(true);
    setFirebaseError(null);
    try {
      // Check if Firebase is available
      if (!db) {
        console.warn('⚠️ Firebase not initialized, using mock data');
        setQuickStatsAgg(null);
        // Use mock data if Firebase is not available
        setStats({
          totalArtists: 25,
          newToday: 3,
          newThisWeek: 8,
          newThisMonth: 15,
          totalSongs: 150,
          totalGigs: 45,
          totalEarnings: 2840
        });
        setArtists([
          { id: '1', artist_name: 'John Smith', email: 'john@example.com', url_slug: 'johnsmith', created_at: '2024-01-15' },
          { id: '2', artist_name: 'Sarah Johnson', email: 'sarah@example.com', url_slug: 'sarahjohnson', created_at: '2024-01-14' },
          { id: '3', artist_name: 'Mike Wilson', email: 'mike@example.com', url_slug: 'mikewilson', created_at: '2024-01-13' }
        ]);
        setFilteredArtists([
          { id: '1', artist_name: 'John Smith', email: 'john@example.com', url_slug: 'johnsmith', created_at: '2024-01-15' },
          { id: '2', artist_name: 'Sarah Johnson', email: 'sarah@example.com', url_slug: 'sarahjohnson', created_at: '2024-01-14' },
          { id: '3', artist_name: 'Mike Wilson', email: 'mike@example.com', url_slug: 'mikewilson', created_at: '2024-01-13' }
        ]);
        setLoading(false);
        return;
      }

      // OPTIMIZATION: Fetch artists first (critical data)
      // Fetch all profiles and filter client-side to handle missing account_type field
      const profilesRef = collection(db, 'profiles');
      const profilesSnapshot = await getDocs(profilesRef);
      const artistsData = profilesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(profile => {
          // Include profiles with account_type 'artist' OR 'karaoke' OR 'agent' OR profiles without account_type (legacy artists)
          // Exclude admin accounts
          return (profile.account_type === 'artist' || profile.account_type === 'karaoke' || profile.account_type === 'agent') || 
                 (!profile.account_type && profile.account_type !== 'admin' && !profile.karaoke_host_id);
        });
      
      setArtists(artistsData);
      setFilteredArtists(artistsData);
      setLoading(false); // Show UI immediately after artists load

      // OPTIMIZATION: Calculate basic stats in parallel
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      const newToday = artistsData.filter(artist => {
        const createdDate = new Date(artist.created_at);
        return createdDate >= today;
      }).length;

      const newThisWeek = artistsData.filter(artist => {
        const createdDate = new Date(artist.created_at);
        return createdDate >= weekAgo;
      }).length;

      const newThisMonth = artistsData.filter(artist => {
        const createdDate = new Date(artist.created_at);
        return createdDate >= monthAgo;
      }).length;

      // OPTIMIZATION: Fetch additional data in parallel
      const [songsSnapshot, gigsSnapshot, requestsSnapshot] = await Promise.all([
        getDocs(collection(db, 'songs')),
        getDocs(collection(db, 'gigs')),
        getDocs(collection(db, 'requests'))
      ]);

      const totalSongs = songsSnapshot.docs.length;
      const totalGigs = gigsSnapshot.docs.length;

      const songCountByArtistId = {};
      songsSnapshot.docs.forEach((docSnap) => {
        const d = docSnap.data();
        if (d.archived) return;
        const uid = d.user_id;
        if (!uid) return;
        songCountByArtistId[uid] = (songCountByArtistId[uid] || 0) + 1;
      });

      const gigCountByArtistId = {};
      gigsSnapshot.docs.forEach((docSnap) => {
        const d = docSnap.data();
        const uid = d.user_id;
        if (!uid) return;
        gigCountByArtistId[uid] = (gigCountByArtistId[uid] || 0) + 1;
      });

      const sessionArtistIdsFromRequests = new Set();
      const revenueByArtistId = {};
      
      // Calculate total earnings and purchase analytics from requests/tips
      // CRITICAL: Only count requests that went through Stripe payment (have payment_intent_id)
      // "Mark As Played" requests (without payment_intent_id) should NOT be counted as paid
      let totalEarnings = 0;
      let totalPurchases = 0;
      const purchasedPrices = [];
      
      requestsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.artist_id && data.session_id) {
          sessionArtistIdsFromRequests.add(data.artist_id);
        }
        const amountPaid = data.amount_paid || 0;
        const tip = data.tip || 0;
        
        // Only count requests that have payment_intent_id (went through Stripe)
        // This excludes "Mark As Played" requests which don't have payment_intent_id
        if (amountPaid > 0 && data.payment_intent_id) {
          totalEarnings += amountPaid;
          totalPurchases++;
          if (data.artist_id) {
            revenueByArtistId[data.artist_id] =
              (revenueByArtistId[data.artist_id] || 0) + amountPaid;
          }
          
          const songPrice = amountPaid - tip;
          if (songPrice > 0) {
            purchasedPrices.push(songPrice);
          }
        }
      });

      setQuickStatsAgg({
        songCountByArtistId,
        gigCountByArtistId,
        revenueByArtistId,
        sessionArtistIdsFromRequests: Array.from(sessionArtistIdsFromRequests),
      });

      // Calculate average, median, and most popular price
      const averageSongPrice = purchasedPrices.length > 0 
        ? purchasedPrices.reduce((sum, price) => sum + price, 0) / purchasedPrices.length 
        : 0;
      
      const sortedPrices = [...purchasedPrices].sort((a, b) => a - b);
      const medianSongPrice = sortedPrices.length > 0
        ? sortedPrices.length % 2 === 0
          ? (sortedPrices[sortedPrices.length / 2 - 1] + sortedPrices[sortedPrices.length / 2]) / 2
          : sortedPrices[Math.floor(sortedPrices.length / 2)]
        : 0;

      const priceFrequency = {};
      purchasedPrices.forEach(price => {
        priceFrequency[price] = (priceFrequency[price] || 0) + 1;
      });
      const mostPopularPrice = Object.keys(priceFrequency).length > 0
        ? Object.entries(priceFrequency).reduce((a, b) => a[1] > b[1] ? a : b)[0]
        : 0;

      // OPTIMIZATION: Defer heavy calculations (time to first live, referrals, ARPU/LTV)
      // These are non-critical and can be calculated in background
      setTimeout(async () => {
        const timeToFirstLiveData = [];
        let totalReferralsCount = 0;

        // Process artists in batches to avoid blocking
        const batchSize = 10;
        for (let i = 0; i < artistsData.length; i += batchSize) {
          const batch = artistsData.slice(i, i + batchSize);
          await Promise.all(batch.map(async (artist) => {
            const createdDate = new Date(artist.created_at);
            
            try {
              const liveStatusDoc = await getDoc(doc(db, 'live_status', artist.id));
              if (liveStatusDoc.exists()) {
                const firstLiveDate = liveStatusDoc.data().session_start_time?.toDate?.() || 
                                     liveStatusDoc.data().updated_at?.toDate?.();
                if (firstLiveDate) {
                  const daysToLive = Math.floor((firstLiveDate - createdDate) / (1000 * 60 * 60 * 24));
                  if (daysToLive >= 0) {
                    timeToFirstLiveData.push(daysToLive);
                  }
                }
              }
            } catch (err) {
              // Silently skip errors
            }

            try {
              const referralsQuery = query(
                collection(db, 'referrals'),
                where('referrerId', '==', artist.id)
              );
              const referralsSnapshot = await getDocs(referralsQuery);
              totalReferralsCount += referralsSnapshot.docs.length;
            } catch (err) {
              // Silently skip errors
            }
          }));
        }

        const averageTimeToFirstLive = timeToFirstLiveData.length > 0
          ? timeToFirstLiveData.reduce((sum, days) => sum + days, 0) / timeToFirstLiveData.length
          : 0;

        let totalRevenuePerArtist = {};
        requestsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          // Only count requests that went through Stripe payment (have payment_intent_id)
          // Exclude "Mark As Played" requests which don't have payment_intent_id
          if (data.artist_id && data.amount_paid > 0 && data.payment_intent_id) {
            totalRevenuePerArtist[data.artist_id] = (totalRevenuePerArtist[data.artist_id] || 0) + (data.amount_paid || 0);
          }
        });

        const artistRevenues = Object.values(totalRevenuePerArtist);
        const averageARPU = artistRevenues.length > 0
          ? artistRevenues.reduce((sum, rev) => sum + rev, 0) / artistRevenues.length
          : 0;

        const averageLTV = averageARPU * Math.max(1, averageTimeToFirstLive);

        // Update stats with deferred calculations
        setStats(prev => ({
          ...prev,
          averageTimeToFirstLive: Number(averageTimeToFirstLive.toFixed(1)),
          totalReferrals: totalReferralsCount,
          averageARPU: Number(averageARPU.toFixed(2)),
          averageLTV: Number(averageLTV.toFixed(2))
        }));
      }, 100);

      // Update stats with immediate calculations
      setStats({
        totalArtists: artistsData.length,
        newToday,
        newThisWeek,
        newThisMonth,
        totalSongs,
        totalGigs,
        totalEarnings: Number(totalEarnings) || 0,
        averageSongPrice: Number(averageSongPrice.toFixed(2)),
        medianSongPrice: Number(medianSongPrice.toFixed(2)),
        mostPopularPrice: Number(mostPopularPrice),
        totalPurchases,
        averageTimeToFirstLive: 0, // Will be updated later
        totalReferrals: 0, // Will be updated later
        averageARPU: 0, // Will be updated later
        averageLTV: 0 // Will be updated later
      });

      // OPTIMIZATION: Fetch live status data in background (non-blocking)
      setTimeout(() => {
        fetchArtistLiveStats(artistsData.map(a => a.id));
      }, 200);

    } catch (error) {
      console.error('Error fetching admin data:', error);
      setFirebaseError(error.message);
      
      setArtists([]);
      setFilteredArtists([]);
      setQuickStatsAgg(null);
      setStats({
        totalArtists: 0,
        newToday: 0,
        newThisWeek: 0,
        newThisMonth: 0,
        totalSongs: 0,
        totalGigs: 0,
        totalEarnings: 0,
        averageSongPrice: 0,
        medianSongPrice: 0,
        mostPopularPrice: 0,
        totalPurchases: 0,
        averageTimeToFirstLive: 0,
        totalReferrals: 0,
        averageARPU: 0,
        averageLTV: 0
      });
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    try {
      // Fetch daily stats for the last 30 days
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      
      // Fetch all artist profiles first to avoid "Unknown" artists
      // Fetch all profiles and filter client-side to handle missing account_type field
      const profilesRef = collection(db, 'profiles');
      const profilesSnapshot = await getDocs(profilesRef);
      const allArtists = profilesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(profile => {
          // Include profiles with account_type 'artist' OR 'karaoke' OR 'agent' OR profiles without account_type (legacy artists)
          // Exclude admin accounts
          return (profile.account_type === 'artist' || profile.account_type === 'karaoke' || profile.account_type === 'agent') || 
                 (!profile.account_type && profile.account_type !== 'admin' && !profile.karaoke_host_id);
        });
      
      // Create a map for quick lookup
      const artistsMap = new Map(allArtists.map(a => [a.id, a]));
      const profileById = new Map(profilesSnapshot.docs.map((d) => [d.id, d.data()]));
      
      // Calculate daily signups
      const dailyStats = {};
      profilesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const createdDate = data.created_at ? new Date(data.created_at).toDateString() : new Date().toDateString();
        dailyStats[createdDate] = (dailyStats[createdDate] || 0) + 1;
      });

      // Get all requests
      const requestsRef = collection(db, 'requests');
      const requestsSnapshot = await getDocs(requestsRef);
      const requestsData = requestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate ? doc.data().created_at.toDate() : new Date(doc.data().created_at || 0)
      }));

      const analyticsRequests = requestsData.filter(isAdminAnalyticsPaidRequest);
      
      // Get top artists by request count
      const artistRequestCounts = {};
      analyticsRequests.forEach(request => {
        const artistId = request.artist_id;
        if (artistId) {
          artistRequestCounts[artistId] = (artistRequestCounts[artistId] || 0) + 1;
        }
      });

      // Get top artists with proper names
      const topArtists = Object.entries(artistRequestCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([artistId, count]) => {
          const artist = artistsMap.get(artistId);
          return {
            id: artistId,
            name: artist?.artist_name || artist?.karaoke_name || 'Unknown',
            requestCount: count
          };
        });

      // Get recent activity (last 20 paid requests, properly sorted by date)
      const recentRequests = analyticsRequests
        .filter(req => req.created_at && req.created_at instanceof Date)
        .sort((a, b) => b.created_at - a.created_at)
        .slice(0, 20)
        .map(request => {
          const artist = artistsMap.get(request.artist_id);
          return {
            id: request.id,
            song_name: request.song_title || request.song_name || 'Unknown Song',
            artistName: artist?.artist_name || artist?.karaoke_name || 'Unknown',
            amount_paid: request.amount_paid || 0,
            status: request.status || 'pending',
            created_at: request.created_at
          };
        });

      let webLogins = [];
      const omitAnalyticsWebLoginEmails = new Set([
        'admin@yoursongrequest.com',
        'juice@juice.com',
      ]);
      try {
        const loginsQ = query(
          collection(db, 'web_login_events'),
          orderBy('created_at', 'desc'),
          limit(200)
        );
        const loginSnap = await getDocs(loginsQ);
        webLogins = loginSnap.docs
          .map((d) => {
            const data = d.data();
            const created = data.created_at?.toDate ? data.created_at.toDate() : null;
            return {
              id: d.id,
              user_id: data.user_id || '',
              email: data.email || '—',
              source_path: data.source_path || '',
              event_type: data.event_type || 'password_login',
              created_at: created,
            };
          })
          .filter((row) => {
            const e = String(row.email || '').trim().toLowerCase();
            return !omitAnalyticsWebLoginEmails.has(e);
          });
      } catch (loginErr) {
        console.warn('Analytics: web_login_events', loginErr);
      }

      let trialDemoCompletions = [];
      try {
        const demoQ = query(
          collection(db, 'artist_analytics_events'),
          where('event_type', '==', ARTIST_ANALYTICS_EVENT_TYPES.LIVE_TRIAL_DEMO_COMPLETED),
          orderBy('created_at', 'desc'),
          limit(200)
        );
        const demoSnap = await getDocs(demoQ);
        trialDemoCompletions = demoSnap.docs
          .map((d) => {
            const data = d.data();
            const created = data.created_at?.toDate ? data.created_at.toDate() : null;
            const prof = profileById.get(data.user_id || '') || {};
            const displayName = prof.artist_name || prof.karaoke_name || 'Unknown';
            return {
              id: d.id,
              user_id: data.user_id || '',
              email: data.email || '—',
              artistName: displayName,
              source_path: data.source_path || '',
              created_at: created,
            };
          })
          .filter((row) => {
            const e = String(row.email || '').trim().toLowerCase();
            return !omitAnalyticsWebLoginEmails.has(e);
          });
      } catch (demoErr) {
        console.warn('Analytics: artist_analytics_events', demoErr);
      }

      setAnalyticsData({
        dailyStats: Object.entries(dailyStats).map(([date, count]) => ({ date, count })),
        topArtists,
        recentActivity: recentRequests,
        revenueTrends: [], // Could be implemented with more complex queries
        userGrowth: Object.entries(dailyStats).map(([date, count]) => ({ date, count })),
        webLogins,
        trialDemoCompletions,
      });

    } catch (error) {
      console.error('Error fetching analytics data:', error);
    }
  };

  const handleArtistSelect = async (artist) => {
    setSelectedArtist(artist);
    setShowArtistDetails(true);
  };

  const handleStripeLegacyRemoved = useCallback((artistId) => {
    const strip = (row) => {
      if (!row || row.id !== artistId) return row;
      const next = { ...row };
      STRIPE_LEGACY_PROFILE_KEYS.forEach((k) => {
        delete next[k];
      });
      return next;
    };
    setArtists((prev) => prev.map((a) => strip(a)));
    setFilteredArtists((prev) => prev.map((a) => strip(a)));
    setSelectedArtist((prev) => strip(prev));
  }, []);

  const handleArtistManage = async (artist) => {
    setSelectedArtist(artist);
    if (artist.account_type === 'agent') {
      setShowAgentManager(true);
      return;
    }
    setShowArtistManager(true);
  };

  const handleArtistUpdate = async (artistId, updates) => {
    try {
      const artistRef = doc(db, 'profiles', artistId);
      await updateDoc(artistRef, {
        ...updates,
        updated_at: new Date().toISOString()
      });
      
      toast({
        title: 'Artist updated',
        description: 'Country and profile changes were saved.',
      });
      // Refresh data
      await fetchAdminData();
      setShowArtistDetails(false);
    } catch (error) {
      console.error('Error updating artist:', error);
      toast({
        title: 'Update failed',
        description: error.message || 'Could not save artist profile.',
        variant: 'destructive',
      });
    }
  };

  const handleArtistDelete = async (artist) => {
    if (!confirm(`Are you sure you want to delete artist "${artist.artist_name || artist.karaoke_name}"? This action cannot be undone and will delete all their data including songs, gigs, and requests.`)) {
      return;
    }

    try {
      // Delete all related data first
      const batch = [];
      
      // Delete songs
      const songsRef = collection(db, 'songs');
      const songsQuery = query(songsRef, where('user_id', '==', artist.id));
      const songsSnapshot = await getDocs(songsQuery);
      songsSnapshot.docs.forEach(doc => {
        batch.push(deleteDoc(doc.ref));
      });

      // Delete gigs
      const gigsRef = collection(db, 'gigs');
      const gigsQuery = query(gigsRef, where('user_id', '==', artist.id));
      const gigsSnapshot = await getDocs(gigsQuery);
      gigsSnapshot.docs.forEach(doc => {
        batch.push(deleteDoc(doc.ref));
      });

      // Delete requests
      const requestsRef = collection(db, 'requests');
      const requestsQuery = query(requestsRef, where('artist_id', '==', artist.id));
      const requestsSnapshot = await getDocs(requestsQuery);
      requestsSnapshot.docs.forEach(doc => {
        batch.push(deleteDoc(doc.ref));
      });

      // Delete the profile
      const profileRef = doc(db, 'profiles', artist.id);
      batch.push(deleteDoc(profileRef));

      // Execute all deletions
      await Promise.all(batch);
      
      // Refresh data
      await fetchAdminData();
      setShowArtistDetails(false);
      setShowArtistManager(false);
      
      alert('Artist and all related data deleted successfully.');
    } catch (error) {
      console.error('Error deleting artist:', error);
      alert('Error deleting artist. Please try again.');
    }
  };

// Force artist offline
  const handleForceOffline = async (artist) => {
    if (!window.confirm(`Force ${artist.artist_name || artist.email} offline? This will immediately end their live session.`)) {
      return;
    }

    if (!db) {
      alert('Firebase is not initialized.');
      return;
    }

    try {
      if (!auth.currentUser) {
        toast({
          title: 'Session Expired',
          description: 'Please sign in again to perform this action.',
          variant: 'destructive'
        });
        return;
      }

      // Check if the live_status document exists
      const liveStatusRef = doc(db, 'live_status', artist.id);
      const liveStatusDoc = await getDoc(liveStatusRef);
      
      if (liveStatusDoc.exists()) {
        // Document exists, update it
        await updateDoc(liveStatusRef, {
          is_live: false,
          session_end_time: Timestamp.now(),
          updated_at: Timestamp.now(),
          force_ended: true,
          force_ended_by: 'admin',
          force_ended_at: Timestamp.now()
        });
      } else {
        // Document doesn't exist, create it as offline
        await setDoc(liveStatusRef, {
          is_live: false,
          session_end_time: Timestamp.now(),
          updated_at: Timestamp.now(),
          force_ended: true,
          force_ended_by: 'admin',
          force_ended_at: Timestamp.now(),
          user_id: artist.id
        });
      }

      // Refresh live stats to reflect the change
      await fetchArtistLiveStats([artist.id]);

      toast({
        title: 'Success',
        description: `Successfully forced ${artist.artist_name || artist.email} offline.`,
      });
    } catch (error) {
      console.error('Error forcing artist offline:', error);
      console.error('Full error details:', {
        message: error.message,
        code: error.code,
        artistId: artist.id,
        artistName: artist.artist_name,
        artistEmail: artist.email
      });
      toast({
        title: 'Error',
        description: `Failed to force artist offline: ${error.message}`,
        variant: 'destructive'
      });
    }
  };

  // Bulk operations handlers
  const handleArtistSelection = (artistId, isSelected) => {
    if (isSelected) {
      setSelectedArtists(prev => [...prev, artistId]);
    } else {
      setSelectedArtists(prev => prev.filter(id => id !== artistId));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedArtists.length === 0) {
      alert('Please select at least one artist.');
      return;
    }

    const selectedArtistData = artists.filter(artist => selectedArtists.includes(artist.id));

    switch (action) {
      case 'notify':
        const message = prompt('Enter notification message:');
        if (message) {
          // In a real implementation, you would send notifications here
          alert(`Notification sent to ${selectedArtists.length} artists: "${message}"`);
          setSelectedArtists([]);
        }
        break;

      case 'export':
        const exportData = selectedArtistData.map(artist => ({
          id: artist.id,
          name: artist.artist_name,
          email: artist.email,
          url_slug: artist.url_slug,
          created_at: artist.created_at,
          account_type: artist.account_type
        }));
        
        const dataStr = JSON.stringify(exportData, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `artists_export_${new Date().toISOString().split('T')[0]}.json`;
        link.click();
        URL.revokeObjectURL(url);
        
        alert(`Exported data for ${selectedArtists.length} artists.`);
        setSelectedArtists([]);
        break;

      case 'archive':
        if (confirm(`Are you sure you want to archive ${selectedArtists.length} artists?`)) {
          try {
            const batch = [];
            selectedArtists.forEach(artistId => {
              const artistRef = doc(db, 'profiles', artistId);
              batch.push(updateDoc(artistRef, { 
                archived: true, 
                archived_at: new Date().toISOString() 
              }));
            });
            
            await Promise.all(batch);
            await fetchAdminData();
            alert(`Successfully archived ${selectedArtists.length} artists.`);
            setSelectedArtists([]);
          } catch (error) {
            console.error('Error archiving artists:', error);
            alert('Error archiving artists. Please try again.');
          }
        }
        break;

      case 'delete':
        const confirmMessage = `⚠️ WARNING: This will permanently delete ${selectedArtists.length} artist account(s) and ALL their data (songs, gigs, requests, etc.). This action cannot be undone.\n\nAre you absolutely sure you want to proceed?`;
        if (confirm(confirmMessage)) {
          try {
            // Delete accounts one by one to handle errors gracefully
            let successCount = 0;
            let errorCount = 0;
            const errors = [];
            
            if (!auth.currentUser) {
              throw new Error('Admin session expired. Please sign in again.');
            }
            const deleteIdToken = await auth.currentUser.getIdToken();

            for (const artistId of selectedArtists) {
              try {
                const response = await fetch('https://deleteuseraccounthttp-2j74a3qova-uc.a.run.app', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${deleteIdToken}`,
                  },
                  body: JSON.stringify({ userId: artistId })
                });
                
                if (!response.ok) {
                  const errorData = await response.json();
                  throw new Error(errorData.error || 'Failed to delete account');
                }
                
                const result = await response.json();
                console.log(`✅ Successfully deleted user ${artistId}:`, result);
                successCount++;
              } catch (error) {
                errorCount++;
                errors.push(`Artist ID ${artistId}: ${error.message}`);
                console.error(`Error deleting artist ${artistId}:`, error);
              }
            }
            
            // Refresh the data
            await fetchAdminData();
            
            // Show results
            if (successCount > 0 && errorCount === 0) {
              alert(`✅ Successfully deleted ${successCount} artist account(s). All associated data has been removed and email addresses are now available for re-signup.`);
            } else if (successCount > 0 && errorCount > 0) {
              alert(`⚠️ Partially completed: ${successCount} account(s) deleted successfully, ${errorCount} failed.\n\nErrors:\n${errors.join('\n')}`);
            } else {
              alert(`❌ Failed to delete any accounts.\n\nErrors:\n${errors.join('\n')}`);
            }
            
            setSelectedArtists([]);
          } catch (error) {
            console.error('Error in batch delete operation:', error);
            alert('Error performing batch delete. Please try again.');
          }
        }
        break;

      case 'force_all_offline':
        const forceAllConfirm = `⚠️ WARNING: This will force ALL artists offline immediately. This action will end all live sessions.\n\nAre you sure you want to proceed?`;
        if (confirm(forceAllConfirm)) {
          setLoading(true);
          try {
            // Get all profiles
            const allProfilesSnapshot = await getDocs(collection(db, 'profiles'));
            const allArtists = allProfilesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            let successCount = 0;
            let errorCount = 0;
            const errors = [];
            
            // Force all artists offline
            for (const artist of allArtists) {
              try {
                const liveStatusRef = doc(db, 'live_status', artist.id);
                const liveStatusDoc = await getDoc(liveStatusRef);
                
                if (liveStatusDoc.exists() && liveStatusDoc.data().is_live) {
                  await updateDoc(liveStatusRef, {
                    is_live: false,
                    session_end_time: Timestamp.now(),
                    updated_at: Timestamp.now(),
                    force_ended: true,
                    force_ended_by: 'admin',
                    force_ended_at: Timestamp.now()
                  });
                  successCount++;
                } else {
                  // Ensure they're marked as offline
                  await setDoc(liveStatusRef, {
                    is_live: false,
                    session_end_time: Timestamp.now(),
                    updated_at: Timestamp.now(),
                    force_ended: true,
                    force_ended_by: 'admin',
                    force_ended_at: Timestamp.now(),
                    user_id: artist.id
                  }, { merge: true });
                  successCount++;
                }
              } catch (error) {
                errorCount++;
                errors.push(`${artist.artist_name || artist.email}: ${error.message}`);
              }
            }
            
            // Specifically force /fitzsy offline and lock it
            const fitzsyProfile = allArtists.find(a => 
              a.url_slug?.toLowerCase() === 'fitzsy' || 
              a.email?.toLowerCase().includes('fitzsy')
            );
            
            if (fitzsyProfile) {
              try {
                const fitzsyLiveStatusRef = doc(db, 'live_status', fitzsyProfile.id);
                await setDoc(fitzsyLiveStatusRef, {
                  is_live: false,
                  session_end_time: Timestamp.now(),
                  updated_at: Timestamp.now(),
                  force_ended: true,
                  force_ended_by: 'admin',
                  force_ended_at: Timestamp.now(),
                  user_id: fitzsyProfile.id,
                  force_keep_offline: true // Lock to prevent going live
                }, { merge: true });
                console.log(`✅ /fitzsy forced offline and locked`);
              } catch (error) {
                console.error('Error locking /fitzsy:', error);
              }
            }
            
            // Refresh data
            await fetchAdminData();
            await fetchArtistLiveStats(allArtists.map(a => a.id));
            
            if (errorCount === 0) {
              toast({
                title: 'Success',
                description: `Successfully forced ${successCount} artist(s) offline. /fitzsy has been locked offline.`,
              });
            } else {
              toast({
                title: 'Partial Success',
                description: `${successCount} forced offline, ${errorCount} failed. /fitzsy has been locked offline.`,
                variant: 'destructive'
              });
            }
          } catch (error) {
            console.error('Error in force all offline operation:', error);
            toast({
              title: 'Error',
              description: 'Error forcing all artists offline. Please try again.',
              variant: 'destructive'
            });
          } finally {
            setLoading(false);
          }
        }
        break;
      case 'force_offline':
        const forceOfflineConfirm = `⚠️ WARNING: This will force ${selectedArtists.length} artist(s) offline immediately. This action will end their live sessions.\n\nAre you sure you want to proceed?`;
        if (confirm(forceOfflineConfirm)) {
          try {
            let successCount = 0;
            let errorCount = 0;
            const errors = [];
            
            for (const artistId of selectedArtists) {
              try {
                if (!auth.currentUser) {
                  throw new Error('Admin session expired. Please sign in again.');
                }

                const liveStatusRef = doc(db, 'live_status', artistId);
                const liveStatusDoc = await getDoc(liveStatusRef);
                
                if (liveStatusDoc.exists()) {
                  await updateDoc(liveStatusRef, {
                    is_live: false,
                    session_end_time: Timestamp.now(),
                    updated_at: Timestamp.now(),
                    force_ended: true,
                    force_ended_by: 'admin',
                    force_ended_at: Timestamp.now()
                  });
                } else {
                  await setDoc(liveStatusRef, {
                    is_live: false,
                    session_end_time: Timestamp.now(),
                    updated_at: Timestamp.now(),
                    force_ended: true,
                    force_ended_by: 'admin',
                    force_ended_at: Timestamp.now(),
                    user_id: artistId
                  });
                }
                
                successCount++;
              } catch (error) {
                errorCount++;
                const artist = artists.find(a => a.id === artistId);
                errors.push(`${artist?.artist_name || artistId}: ${error.message}`);
                console.error(`Error forcing artist ${artistId} offline:`, error);
              }
            }
            
            // Refresh live stats
            await fetchArtistLiveStats(selectedArtists);
            
            // Show results
            if (successCount > 0 && errorCount === 0) {
              toast({
                title: 'Success',
                description: `Successfully forced ${successCount} artist(s) offline.`,
                variant: 'default'
              });
            } else if (successCount > 0 && errorCount > 0) {
              toast({
                title: 'Partial Success',
                description: `${successCount} forced offline, ${errorCount} failed.`,
                variant: 'destructive'
              });
            } else {
              toast({
                title: 'Error',
                description: `Failed to force artists offline.\n\nErrors:\n${errors.join('\n')}`,
                variant: 'destructive'
              });
            }
            
            setSelectedArtists([]);
          } catch (error) {
            console.error('Error in bulk force offline operation:', error);
            toast({
              title: 'Error',
              description: 'Error performing bulk force offline. Please try again.',
              variant: 'destructive'
            });
          }
        }
        break;

      default:
        alert('Unknown bulk action.');
    }
  };

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-white mt-4">Checking session...</p>
        </div>
      </div>
    );
  }

  if (!isAdminAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-white mt-4">Redirecting to admin login...</p>
        </div>
      </div>
    );
  }

  // Second factor. Enrolment is mandatory, so a console that has never been set
  // up lands here first and cannot be skipped past.
  if (needsMfaEnrollment) {
    return <AdminMfaEnrollment onComplete={refreshMfaState} />;
  }

  if (needsMfaChallenge) {
    return <AdminMfaChallenge onVerified={refreshMfaState} onCancel={adminSignOut} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500 mx-auto"></div>
          <p className="text-white mt-4">Loading admin data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-900 text-white ${mobile.isMobile ? 'pb-20' : ''}`}>
      {/* Header - Mobile Optimized */}
      <header className={`bg-gray-800 border-b border-gray-700 ${mobile.isMobile ? 'p-3 sticky top-0 z-50' : 'p-4'}`}>
        <div className="max-w-7xl mx-auto">
          <div className={`flex ${mobile.isMobile ? 'flex-col' : 'flex-row'} items-start ${mobile.isMobile ? '' : 'sm:items-center'} justify-between ${mobile.isMobile ? 'space-y-3' : 'space-y-4 sm:space-y-0'}`}>
            <div className="flex items-center space-x-3">
              <div className={`${mobile.isMobile ? 'w-8 h-8' : 'w-8 h-8 sm:w-10 sm:h-10'} bg-gradient-to-r from-red-500 to-orange-500 rounded-lg flex items-center justify-center`}>
                <Shield className={`${mobile.isMobile ? 'w-5 h-5' : 'w-5 h-5 sm:w-6 sm:h-6'} text-white`} />
              </div>
              <div>
                <h1 className={`${mobile.isMobile ? 'text-lg' : 'text-xl sm:text-2xl'} font-bold`}>Admin Console</h1>
                <p className={`text-gray-400 ${mobile.isMobile ? 'text-xs' : 'text-xs sm:text-sm'}`}>Your Song Request Administration</p>
              </div>
            </div>
            
            <div className={`flex ${mobile.isMobile ? 'flex-row w-full justify-between' : 'flex-col sm:flex-row'} items-start ${mobile.isMobile ? '' : 'sm:items-center'} ${mobile.isMobile ? '' : 'space-y-2 sm:space-y-0 sm:space-x-4'} ${mobile.isMobile ? '' : 'w-full sm:w-auto'}`}>
              <Badge variant="outline" className={`border-green-500 text-green-400 ${mobile.isMobile ? 'text-xs' : 'text-xs'}`}>
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                Admin Access
              </Badge>
              <Button
                onClick={adminSignOut}
                variant="outline"
                size="sm"
                className={`border-red-500 text-red-400 hover:bg-red-500 hover:text-white ${mobile.isMobile ? 'flex-1' : 'w-full sm:w-auto'}`}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className={`max-w-7xl mx-auto ${mobile.isMobile ? 'p-3' : 'p-4 sm:p-6'}`}>
        {/* Firebase Error Display */}
        {(firebaseError || !db) && (
          <div className="mb-6 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
            <div className="flex items-center space-x-3">
              <Shield className="w-6 h-6 text-yellow-400" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-300">Admin Console - Demo Mode</h3>
                <p className="text-yellow-200 text-sm mt-1">
                  {firebaseError ? `Unable to connect to Firebase: ${firebaseError}` : 'Firebase not configured - using demo data'}
                </p>
                <p className="text-yellow-200 text-sm mt-1">
                  The admin console is running in demo mode with sample data. All features are available for demonstration.
                </p>
                <p className="text-yellow-200 text-sm mt-1">
                  To enable full functionality, please configure Firebase in your .env file.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Tabs - Mobile Optimized */}
        <div className={`flex ${mobile.isMobile ? 'flex-wrap gap-2' : 'flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-1'} mb-6 sm:mb-8 bg-gray-800 ${mobile.isMobile ? 'p-2' : 'p-1'} rounded-lg ${mobile.isMobile ? 'w-full' : 'w-full sm:w-fit'}`}>
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'artists', label: 'Artists', icon: Users },
            { id: 'sessions', label: 'Sessions', icon: Calendar },
            { id: 'gigs', label: 'Gigs', icon: Music },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp },
            { id: 'competition', label: 'Competition', icon: Radio },
            { id: 'communication', label: 'Communication', icon: MessageCircle },
            { id: 'referrals', label: 'Referrals', icon: Gift },
            { id: 'store', label: 'Store', icon: ShoppingBag },
            { id: 'qr-banners', label: 'QR Banners', icon: QrCode },
            { id: 'financial', label: 'Financial', icon: DollarSign }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center ${mobile.isMobile ? '' : 'sm:justify-start'} space-x-2 ${mobile.isMobile ? 'px-3 py-2 text-xs' : 'px-3 sm:px-4 py-2'} rounded-md transition-colors ${mobile.isMobile ? 'text-xs' : 'text-sm sm:text-base'} ${
                activeTab === tab.id
                  ? 'bg-red-500 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700'
              }`}
            >
              <tab.icon className={`${mobile.isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Statistics Cards - Mobile Optimized */}
            <div className={`grid ${mobile.isMobile ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'} gap-4 sm:gap-6`}>
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Artists</p>
                      <p className="text-3xl font-bold text-white">{stats.totalArtists}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">New Today</p>
                      <p className="text-3xl font-bold text-white">{stats.newToday}</p>
                    </div>
                    <UserPlus className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">New This Week</p>
                      <p className="text-3xl font-bold text-white">{stats.newThisWeek}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">New This Month</p>
                      <p className="text-3xl font-bold text-white">{stats.newThisMonth}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-orange-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Songs</p>
                      <p className="text-2xl font-bold text-white">{stats.totalSongs}</p>
                    </div>
                    <Music className="w-6 h-6 text-pink-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Gigs</p>
                      <p className="text-2xl font-bold text-white">{stats.totalGigs}</p>
                    </div>
                    <Calendar className="w-6 h-6 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Earnings</p>
                      <p className="text-2xl font-bold text-white">{format(stats.totalEarnings || 0)}</p>
                    </div>
                    <DollarSign className="w-6 h-6 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Advanced Analytics - Price & Purchase Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Avg Song Price</p>
                      <p className="text-2xl font-bold text-white">{format(stats.averageSongPrice)}</p>
                      <p className="text-xs text-gray-500 mt-1">Mean price</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Median Price</p>
                      <p className="text-2xl font-bold text-white">{format(stats.medianSongPrice)}</p>
                      <p className="text-xs text-gray-500 mt-1">Middle price</p>
                    </div>
                    <BarChart3 className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Most Popular</p>
                      <p className="text-2xl font-bold text-white">{format(stats.mostPopularPrice)}</p>
                      <p className="text-xs text-gray-500 mt-1">Frequent price</p>
                    </div>
                    <Music className="w-8 h-8 text-pink-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Purchases</p>
                      <p className="text-2xl font-bold text-white">{stats.totalPurchases}</p>
                      <p className="text-xs text-gray-500 mt-1">Songs bought</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Artist Engagement Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Avg Time to Live</p>
                      <p className="text-2xl font-bold text-white">{stats.averageTimeToFirstLive.toFixed(1)}</p>
                      <p className="text-xs text-gray-500 mt-1">Days until first live</p>
                    </div>
                    <AdminClockIcon className="w-8 h-8 text-yellow-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Total Referrals</p>
                      <p className="text-2xl font-bold text-white">{stats.totalReferrals}</p>
                      <p className="text-xs text-gray-500 mt-1">Artist referrals</p>
                    </div>
                    <Gift className="w-8 h-8 text-orange-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Avg ARPU</p>
                      <p className="text-2xl font-bold text-white">{format(stats.averageARPU)}</p>
                      <p className="text-xs text-gray-500 mt-1">Revenue per artist</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-400 text-sm">Avg LTV</p>
                      <p className="text-2xl font-bold text-white">{format(stats.averageLTV)}</p>
                      <p className="text-xs text-gray-500 mt-1">Lifetime value</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Artists Tab */}
        {activeTab === 'artists' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Search and Filters */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-6">
                <div className="flex flex-col space-y-4">
                  {/* Search and Sort Controls */}
                  <div className="flex flex-col lg:flex-row items-start lg:items-center space-y-3 lg:space-y-0 lg:space-x-4">
                    <div className="flex-1 w-full">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Search by Artist Name, Email, Full Name, Stripe ID, Artist ID, URL Slug..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10 bg-gray-700 border-gray-600 text-white placeholder-gray-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                    
                    {/* Sort Controls */}
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-400 whitespace-nowrap">Sort by:</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                          className="bg-gray-700 border-gray-600 text-white text-sm rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="name">Name</option>
                          <option value="email">Email</option>
                          <option value="date">Date Signed Up</option>
                        </select>
                      </div>
                      
                      <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors"
                        title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                      >
                        {sortOrder === 'asc' ? (
                          <>
                            <span className="text-sm">A-Z</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            <span className="text-sm">Z-A</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {/* Live Status Filter */}
                  <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-gray-700">
                    <div className="flex items-center space-x-2">
                      <Filter className="w-4 h-4 text-gray-400" />
                      <label className="text-sm text-gray-400 whitespace-nowrap">Filter by Status:</label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setLiveStatusFilter('all')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          liveStatusFilter === 'all'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        All Artists
                      </button>
                      <button
                        onClick={() => setLiveStatusFilter('live')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          liveStatusFilter === 'live'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        <Radio className="w-3 h-3 inline mr-1" />
                        Currently Live
                      </button>
                      <button
                        onClick={() => setLiveStatusFilter('offline')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          liveStatusFilter === 'offline'
                            ? 'bg-yellow-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Previously Live
                      </button>
                      <button
                        onClick={() => setLiveStatusFilter('never_live')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          liveStatusFilter === 'never_live'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Never Gone Live
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-dashed border-gray-600">
                    <div className="flex items-center space-x-2">
                      <Mic className="w-4 h-4 text-cyan-400" />
                      <label className="text-sm text-gray-400 whitespace-nowrap">Account type:</label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setAccountTypeFilter('all')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          accountTypeFilter === 'all'
                            ? 'bg-slate-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        All types
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountTypeFilter('artist')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          accountTypeFilter === 'artist'
                            ? 'bg-purple-600 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Artists
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountTypeFilter('karaoke')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          accountTypeFilter === 'karaoke'
                            ? 'bg-cyan-700 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Karaoke
                      </button>
                      <button
                        type="button"
                        onClick={() => setAccountTypeFilter('agent')}
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${
                          accountTypeFilter === 'agent'
                            ? 'bg-blue-700 text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        Agents
                      </button>
                    </div>
                  </div>
                  
                  {/* Results Count and Bulk Actions */}
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs sm:text-sm">
                      {filteredArtists.length} artists
                    </Badge>
                    <Button
                      onClick={() => setBulkOperationMode(!bulkOperationMode)}
                      variant="outline"
                      size="sm"
                      className={bulkOperationMode ? "bg-blue-600 text-white border-blue-500" : "border-gray-600 text-gray-400"}
                    >
                      {bulkOperationMode ? "Exit Bulk Mode" : "Bulk Operations"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bulk Actions Bar */}
            {bulkOperationMode && (
              <Card className="bg-blue-900/20 border-blue-700">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
                    <div className="flex items-center space-x-3">
                      <Badge variant="outline" className="border-blue-500 text-blue-400">
                        {selectedArtists.length} selected
                      </Badge>
                      <span className="text-blue-300 text-sm">
                        Select artists to perform bulk operations
                      </span>
                    </div>
                    {selectedArtists.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction('notify')}
                          className="border-green-500 text-green-400 hover:bg-green-500 hover:text-white"
                        >
                          Send Notification
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction('export')}
                          className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                        >
                          Export Data
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction('archive')}
                          className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white"
                        >
                          Archive
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction('force_offline')}
                          className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                        >
                          <Radio className="w-4 h-4 mr-1" />
                          Force Selected Offline
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction('force_all_offline')}
                          className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-semibold"
                        >
                          <Radio className="w-4 h-4 mr-1" />
                          Force ALL Offline
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleBulkAction('delete')}
                          className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                        >
                          Delete Accounts
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSelectedArtists([])}
                          className="border-gray-500 text-gray-400 hover:bg-gray-500 hover:text-white"
                        >
                          Clear Selection
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Artists List */}
            <div className="space-y-4">
              {/* Sort Indicator */}
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Artists</h3>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <span>Sorted by:</span>
                  <Badge variant="outline" className="border-gray-600 text-gray-300">
                    {sortBy === 'name' && 'Name'}
                    {sortBy === 'email' && 'Email'}
                    {sortBy === 'date' && 'Date Signed Up'}
                    {sortOrder === 'asc' ? ' ↑' : ' ↓'}
                  </Badge>
                </div>
              </div>
              
              <div className={`grid ${mobile.isMobile ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'} gap-4 sm:gap-6`}>
              {filteredArtists.map((artist) => (
                <Card 
                  key={artist.id} 
                  className={`bg-gray-800 border-gray-700 hover:border-gray-600 transition-colors ${
                    selectedArtists.includes(artist.id) ? 'ring-2 ring-blue-500 border-blue-500' : ''
                  }`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        {bulkOperationMode && (
                          <input
                            type="checkbox"
                            checked={selectedArtists.includes(artist.id)}
                            onChange={(e) => handleArtistSelection(artist.id, e.target.checked)}
                            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                          />
                        )}
                        <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {(artist.artist_name || artist.karaoke_name)?.charAt(0) || 'A'}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-semibold text-white">{artist.artist_name || artist.karaoke_name || 'Unnamed Artist'}</h3>
                          <p className="text-sm text-gray-400">{artist.email}</p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        {(() => {
                          const stats = artistLiveStats[artist.id];
                          const isLive = stats?.isCurrentlyLive || false;
                          const hasBeenLive = stats?.hasEverGoneLive || false;
                          const isKaraoke = artist.account_type === 'karaoke';
                          const isAgent = artist.account_type === 'agent';

                          if (isAgent) {
                            return (
                              <div className="flex items-center space-x-1">
                                <Badge variant="outline" className="border-blue-500 text-blue-400 bg-blue-500/10">
                                  <Briefcase className="w-3 h-3 mr-1" />
                                  AGENT
                                </Badge>
                              </div>
                            );
                          }
                          
                          return isLive ? (
                            <div 
                              className="relative"
                              onMouseEnter={() => setHoveredArtist(artist.id)}
                              onMouseLeave={() => setHoveredArtist(null)}
                            >
                              <div className="flex items-center space-x-1">
                                <Badge variant="outline" className="border-green-500 text-green-400 bg-green-500/10 cursor-pointer">
                                  <Radio className="w-3 h-3 mr-1" />
                                  LIVE
                                </Badge>
                                {isKaraoke && (
                                  <Mic className="w-4 h-4 text-purple-400" title="Karaoke Mode" />
                                )}
                              </div>
                              
                              {/* Hover Dropdown */}
                              {hoveredArtist === artist.id && (
                                <motion.div 
                                  initial={{ opacity: 0, y: -10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{ duration: 0.2 }}
                                  className="absolute right-0 mt-2 w-48 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden"
                                >
                                  <button
                                    onClick={() => {
                                      setHoveredArtist(null);
                                      handleForceOffline(artist);
                                    }}
                                    className="w-full px-4 py-3 text-left text-sm text-white hover:bg-orange-500/20 transition-colors flex items-center space-x-2 group"
                                  >
                                    <AlertCircle className="w-4 h-4 text-orange-400 group-hover:text-orange-300" />
                                    <span className="flex-1">Force Offline</span>
                                  </button>
                                </motion.div>
                              )}
                            </div>
                          ) : hasBeenLive ? (
                            <div className="flex items-center space-x-1">
                              <Badge variant="outline" className="border-gray-500 text-gray-400">
                                Active
                              </Badge>
                              {isKaraoke && (
                                <Mic className="w-4 h-4 text-purple-400" title="Karaoke Mode" />
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <Badge variant="outline" className="border-orange-500 text-orange-400">
                                Inactive
                              </Badge>
                              {isKaraoke && (
                                <Mic className="w-4 h-4 text-purple-400" title="Karaoke Mode" />
                              )}
                            </div>
                          );
                        })()}
                        {artistLiveStats[artist.id]?.totalSessions > 0 && (
                          <span className="text-xs text-gray-500">
                            {artistLiveStats[artist.id].totalSessions} session{artistLiveStats[artist.id].totalSessions > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-400">
                        <Globe className="w-4 h-4 mr-2" />
                        <span>
                          {artist.account_type === 'agent'
                            ? `Invite: /signup?agent=${artist.agent_code || 'no-code'}`
                            : `yoursongrequest.com/${artist.url_slug || 'no-url'}`}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-400">
                        <AdminClockIcon className="w-4 h-4 mr-2" />
                        <span>Joined {formatUkDate(artist.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleArtistSelect(artist)}
                        className="flex-1 border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white text-xs sm:text-sm"
                      >
                        <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleArtistManage(artist)}
                        className="flex-1 border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white text-xs sm:text-sm"
                      >
                        <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        {artist.account_type === 'agent' ? 'Manage Agent' : 'Manage'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleArtistDelete(artist)}
                        className="flex-1 border-red-500 text-red-400 hover:bg-red-500 hover:text-white text-xs sm:text-sm"
                      >
                        <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              </div>
            </div>
          </motion.div>
        )}

{/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Enhanced Analytics Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Revenue Analytics */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <DollarSign className="w-5 h-5" />
                    <span>Revenue Analytics</span>
                  </CardTitle>
                  <CardDescription>Financial performance and trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <p className="text-2xl font-bold text-green-400">{format(stats.totalEarnings)}</p>
                        <p className="text-sm text-gray-400">Total Revenue</p>
                      </div>
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <p className="text-2xl font-bold text-blue-400">{format(stats.totalEarnings / Math.max(stats.totalArtists, 1))}</p>
                        <p className="text-sm text-gray-400">Avg per Artist</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">This Month:</span>
                        <span className="text-white">{format(stats.totalEarnings * 0.3)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Last Month:</span>
                        <span className="text-white">{format(stats.totalEarnings * 0.25)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Growth Rate:</span>
                        <span className="text-green-400">+20%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User Engagement */}
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="w-5 h-5" />
                    <span>User Engagement</span>
                  </CardTitle>
                  <CardDescription>Platform usage and activity metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <p className="text-2xl font-bold text-purple-400">{stats.totalSongs}</p>
                        <p className="text-sm text-gray-400">Total Songs</p>
                      </div>
                      <div className="text-center p-3 bg-gray-700 rounded-lg">
                        <p className="text-2xl font-bold text-orange-400">{stats.totalGigs}</p>
                        <p className="text-sm text-gray-400">Total Gigs</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Active Users:</span>
                        <span className="text-white">{Math.floor(stats.totalArtists * 0.8)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Avg Songs/Artist:</span>
                        <span className="text-white">{(stats.totalSongs / Math.max(stats.totalArtists, 1)).toFixed(1)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Avg Gigs/Artist:</span>
                        <span className="text-white">{(stats.totalGigs / Math.max(stats.totalArtists, 1)).toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Web sign-in activity — collapsible; list capped to ~4 rows visible + scroll */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2 space-y-0">
                <button
                  type="button"
                  onClick={() => setWebLoginsSectionOpen((o) => !o)}
                  className="flex w-full items-start justify-between gap-3 rounded-lg p-1 text-left transition-colors hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                  aria-expanded={webLoginsSectionOpen}
                  id="web-logins-section-toggle"
                >
                  <CardTitle className="mb-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-base font-semibold sm:text-lg">
                    <LogIn className="h-5 w-5 shrink-0 text-cyan-400" />
                    <span>Web sign-ins and visits</span>
                    {analyticsData.webLogins?.length > 0 ? (
                      <Badge
                        variant="outline"
                        className="border-gray-500 text-[11px] font-normal text-gray-300"
                      >
                        {analyticsData.webLogins.length}
                      </Badge>
                    ) : null}
                  </CardTitle>
                  {webLoginsSectionOpen ? (
                    <ChevronUp className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  ) : (
                    <ChevronDown className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  )}
                </button>
                {webLoginsSectionOpen ? (
                  <CardDescription className="mt-3">
                    <strong className="text-gray-200">Sign-in:</strong> email/password login.{' '}
                    <strong className="text-gray-200">Returned:</strong> user already had a saved session
                    and opened the site (e.g. dashboard). Once per browser tab per visit wave; password
                    sign-in resets the tab flag. Newest first.
                  </CardDescription>
                ) : null}
              </CardHeader>
              {webLoginsSectionOpen ? (
                <CardContent className="pt-0">
                  <div
                    className="max-h-[24rem] space-y-2 overflow-y-auto overscroll-contain pr-1 [scrollbar-gutter:stable]"
                    role="region"
                    aria-labelledby="web-logins-section-toggle"
                  >
                    {analyticsData.webLogins?.length > 0 ? (
                      analyticsData.webLogins.map((row) => (
                        <div
                          key={row.id}
                          className="flex flex-col gap-1 rounded-lg border border-gray-600/50 bg-gray-700/80 p-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate font-medium text-white">{row.email}</p>
                              {row.event_type === 'session_resume' ? (
                                <Badge className="shrink-0 border-cyan-600/50 bg-cyan-900/50 text-[10px] text-cyan-200">
                                  Returned
                                </Badge>
                              ) : (
                                <Badge className="shrink-0 border-emerald-600/50 bg-emerald-900/50 text-[10px] text-emerald-200">
                                  Sign-in
                                </Badge>
                              )}
                            </div>
                            <p
                              className="truncate font-mono text-xs text-gray-500"
                              title={row.user_id}
                            >
                              {row.user_id}
                            </p>
                            {row.source_path ? (
                              <p className="mt-0.5 truncate text-xs text-purple-300/90">
                                {row.source_path}
                              </p>
                            ) : null}
                          </div>
                          <div className="shrink-0 text-sm text-gray-300 sm:text-right">
                            {row.created_at instanceof Date && !Number.isNaN(row.created_at.getTime())
                              ? formatUkDateTime(row.created_at)
                              : '—'}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="py-6 text-center text-gray-400">
                        No events yet. Rows appear after web sign-in or when a user with a saved session
                        opens the app.
                      </p>
                    )}
                  </div>
                </CardContent>
              ) : null}
            </Card>

            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-2 space-y-0">
                <button
                  type="button"
                  onClick={() => setTrialDemoSectionOpen((o) => !o)}
                  className="flex w-full items-start justify-between gap-3 rounded-lg p-1 text-left transition-colors hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
                  aria-expanded={trialDemoSectionOpen}
                  id="trial-demo-section-toggle"
                >
                  <CardTitle className="mb-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-base font-semibold sm:text-lg">
                    <Sparkles className="h-5 w-5 shrink-0 text-violet-400" />
                    <span>Live trial demo completed</span>
                    {analyticsData.trialDemoCompletions?.length > 0 ? (
                      <Badge
                        variant="outline"
                        className="border-gray-500 text-[11px] font-normal text-gray-300"
                      >
                        {analyticsData.trialDemoCompletions.length}
                      </Badge>
                    ) : null}
                  </CardTitle>
                  {trialDemoSectionOpen ? (
                    <ChevronUp className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  ) : (
                    <ChevronDown className="mt-0.5 h-5 w-5 shrink-0 text-gray-400" />
                  )}
                </button>
                {trialDemoSectionOpen ? (
                  <CardDescription className="mt-3">
                    Fired when an artist finishes the in-app <strong className="text-gray-200">“First time going live?”</strong>{' '}
                    walkthrough on the Live Dashboard (not Admin <strong className="text-gray-200">Demo</strong> lines). In Manage Artist, the circled <strong className="text-gray-200">D</strong> beside Demo means this walkthrough was completed. Newest first.
                  </CardDescription>
                ) : null}
              </CardHeader>
              {trialDemoSectionOpen ? (
                <CardContent className="pt-0">
                  <div
                    className="max-h-[24rem] space-y-2 overflow-y-auto overscroll-contain pr-1 [scrollbar-gutter:stable]"
                    role="region"
                    aria-labelledby="trial-demo-section-toggle"
                  >
                    {analyticsData.trialDemoCompletions?.length > 0 ? (
                      analyticsData.trialDemoCompletions.map((row) => (
                        <div
                          key={row.id}
                          className="flex flex-col gap-1 rounded-lg border border-gray-600/50 bg-gray-700/80 p-3 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate font-medium text-white">{row.artistName}</p>
                              <Badge className="shrink-0 border-violet-600/50 bg-violet-900/40 text-[10px] text-violet-200">
                                Demo done
                              </Badge>
                            </div>
                            <p className="truncate text-sm text-gray-400">{row.email}</p>
                            <p
                              className="truncate font-mono text-xs text-gray-500"
                              title={row.user_id}
                            >
                              {row.user_id}
                            </p>
                            {row.source_path ? (
                              <p className="mt-0.5 truncate text-xs text-purple-300/90">{row.source_path}</p>
                            ) : null}
                          </div>
                          <div className="shrink-0 text-sm text-gray-300 sm:text-right">
                            {row.created_at instanceof Date && !Number.isNaN(row.created_at.getTime())
                              ? formatUkDateTime(row.created_at)
                              : '—'}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="py-6 text-center text-gray-400">
                        No completions yet. Events appear when artists finish the full Live Dashboard trial demo.
                      </p>
                    )}
                  </div>
                </CardContent>
              ) : null}
            </Card>

            {/* Top Artists */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <TrendingUp className="w-5 h-5" />
                  <span>Top Artists by Requests</span>
                </CardTitle>
                <CardDescription>Most requested artists on the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analyticsData.topArtists.length > 0 ? (
                    analyticsData.topArtists.map((artist, index) => (
                      <div key={artist.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center text-white font-bold">
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold text-white">{artist.name}</p>
                            <p className="text-sm text-gray-400">{artist.requestCount} requests</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="bg-green-900/20 text-green-400 border-green-700">
                          #{index + 1}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-center py-4">No request data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AdminClockIcon className="w-5 h-5" />
                  <span>Recent Activity</span>
                </CardTitle>
                <CardDescription>Latest song requests and platform activity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analyticsData.recentActivity.length > 0 ? (
                    analyticsData.recentActivity.map((activity) => (
                      <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <Music className="w-5 h-5 text-blue-400" />
                          <div>
                            <p className="font-semibold text-white">{activity.song_name}</p>
                            <p className="text-sm text-gray-400">
                              Requested for {activity.artistName} • {formatUkDateTime(activity.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-green-400">{format(Number(activity.amount_paid) || 0)}</p>
                          <p className="text-xs text-gray-400">
                            {activity.status === 'completed' ? 'Completed' : 'Pending'}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-center py-4">No recent activity</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* User Growth */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <UserPlus className="w-5 h-5" />
                  <span>User Growth (Last 30 Days)</span>
                </CardTitle>
                <CardDescription>Daily artist signups and growth trends</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analyticsData.userGrowth.length > 0 ? (
                    analyticsData.userGrowth.slice(-7).map((day) => (
                      <div key={day.date} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                        <div>
                          <p className="font-semibold text-white">{formatUkDate(day.date)}</p>
                          <p className="text-sm text-gray-400">{day.count} new artists</p>
                        </div>
                        <div className="w-20 bg-gray-600 rounded-full h-2">
                          <div 
                            className="bg-blue-500 h-2 rounded-full" 
                            style={{ width: `${Math.min((day.count / Math.max(...analyticsData.userGrowth.map(d => d.count))) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-400 text-center py-4">No growth data available</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Platform Health */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Globe className="w-5 h-5" />
                    <span>Platform Health</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Active Artists</span>
                      <span className="text-white font-semibold">{stats.totalArtists}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total Songs</span>
                      <span className="text-white font-semibold">{stats.totalSongs}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total Gigs</span>
                      <span className="text-white font-semibold">{stats.totalGigs}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Total Revenue</span>
                      <span className="text-green-400 font-semibold">{format(stats.totalEarnings || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <BarChart3 className="w-5 h-5" />
                    <span>Quick Stats</span>
                  </CardTitle>
                  <CardDescription className="text-gray-500 text-xs leading-relaxed">
                    Averages exclude dormant accounts (signed up, no catalogue, no gigs, no live session).
                    Songs: artists with at least one catalogue song. Gigs: artists with at least one gig.
                    Revenue: artists with at least one live session (session history or requests with a session).
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Avg Songs per Artist</span>
                      <span className="text-white font-semibold">
                        {quickStatsDisplay.catalogueArtistCount > 0
                          ? quickStatsDisplay.avgSongsPerCatalogueArtist.toFixed(1)
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Avg Gigs per Artist</span>
                      <span className="text-white font-semibold">
                        {quickStatsDisplay.gigArtistCount > 0
                          ? quickStatsDisplay.avgGigsPerGigArtist.toFixed(1)
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Avg Revenue per Artist</span>
                      <span className="text-green-400 font-semibold">
                        {quickStatsDisplay.liveSessionArtistCount > 0
                          ? format(quickStatsDisplay.avgRevenuePerLiveArtist)
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400">Growth Rate (7 days)</span>
                      <span className="text-blue-400 font-semibold">
                        {stats.newThisWeek > 0 ? '+' : ''}{stats.newThisWeek} artists
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Competition Tab */}
        {activeTab === 'competition' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Competition Management</CardTitle>
                <CardDescription>Enable and configure the artist competition</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* Enable/Disable Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Radio className="w-6 h-6 text-purple-400" />
                      <div>
                        <h3 className="text-white font-semibold">Enable Competition</h3>
                        <p className="text-gray-400 text-sm">Turn the competition on/off for all artists</p>
                      </div>
                    </div>
                    <Switch
                      checked={competitionEnabled}
                      onCheckedChange={async (checked) => {
                        setCompetitionEnabled(checked);
                        // Save immediately when toggle changes
                        try {
                          const competitionRef = doc(db, 'competitions', 'current');
                          await setDoc(competitionRef, {
                            enabled: checked,
                            updatedAt: new Date().toISOString()
                          }, { merge: true });
                          toast({
                            title: checked ? "Competition Enabled" : "Competition Disabled",
                            description: checked ? "Competition banner is now visible to artists" : "Competition banner is now hidden",
                            variant: "default"
                          });
                        } catch (error) {
                          console.error('Error saving competition toggle:', error);
                          toast({
                            title: "Error",
                            description: "Could not update competition status",
                            variant: "destructive"
                          });
                          // Revert on error
                          setCompetitionEnabled(!checked);
                        }
                      }}
                    />
                  </div>

                  {/* Dates & Prize Configuration */}
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-300">Start Date & Time (UTC)</label>
                      <Input
                        type="datetime-local"
                        value={competitionStart || ''}
                        onChange={(e) => setCompetitionStart(e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      {competitionStart && (
                        <p className="text-xs text-gray-400">
                          Selected: {datetimeLocalToIsoUtc(competitionStart) ? `${toDisplayGmt(datetimeLocalToIsoUtc(competitionStart))} UTC` : 'Invalid date'}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-300">End Date & Time (UTC)</label>
                      <Input
                        type="datetime-local"
                        value={competitionEnd || ''}
                        onChange={(e) => setCompetitionEnd(e.target.value)}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                      {competitionEnd && (
                        <p className="text-xs text-gray-400">
                          Selected: {datetimeLocalToIsoUtc(competitionEnd) ? `${toDisplayGmt(datetimeLocalToIsoUtc(competitionEnd))} UTC` : 'Invalid date'}
                        </p>
                      )}
                    </div>
                    
                    <label className="text-sm font-medium text-gray-300">Prize Description</label>
                    <Input
                      value={competitionPrize}
                      onChange={(e) => setCompetitionPrize(e.target.value)}
                      onBlur={async () => {
                        // Auto-save prize description when field loses focus
                        if (competitionPrize.trim()) {
                          try {
                            const competitionRef = doc(db, 'competitions', 'current');
                            await setDoc(competitionRef, {
                              prize: competitionPrize.trim(),
                              updatedAt: new Date().toISOString()
                            }, { merge: true });
                            toast({
                              title: "Prize Updated",
                              description: "Competition prize description has been updated",
                              variant: "default",
                              duration: 2000
                            });
                          } catch (error) {
                            console.error('Error saving prize description:', error);
                            toast({
                              title: "Error",
                              description: "Could not update prize description",
                              variant: "destructive"
                            });
                          }
                        }
                      }}
                      placeholder="Enter prize description..."
                      className="bg-gray-700 border-gray-600 text-white"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <Button
                      onClick={async () => {
                        try {
                          if (!auth.currentUser) {
                            alert("Admin session expired. Please sign in again.");
                            return;
                          }

                          await setDoc(doc(db, "competitions", "current"), {
                            enabled: competitionEnabled,
                            prize: competitionPrize,
                            startAt: datetimeLocalToIsoUtc(competitionStart),
                            endAt: datetimeLocalToIsoUtc(competitionEnd),
                            updatedAt: new Date().toISOString()
                          }, { merge: true });

                          alert("Competition settings saved successfully.");
                        } catch (e) {
                          console.error("Error saving competition settings:", e);
                          alert(
                            e.code === "permission-denied"
                              ? "Permission denied. Your account does not have admin access."
                              : `Failed to save: ${e.message || "Unknown error"}`
                          );
                        }
                      }}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Save Settings
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => alert('Preview competition as artist')}
                      className="border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white"
                    >
                      Preview as Artist
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Leaderboard */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white">Current Leaderboard</CardTitle>
                    <CardDescription>Top artists in the competition</CardDescription>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      onClick={async () => {
                        setLoadingCompetition(true);
                        try {
                          // Force refresh by re-querying
                          const leaderboardQuery = query(
                            collection(db, 'competitionPoints'),
                            where('totalPoints', '>', 0),
                            orderBy('totalPoints', 'desc'),
                            limit(20)
                          );
                          const snapshot = await getDocs(leaderboardQuery);
                          const enriched = [];
                          for (const docSnap of snapshot.docs) {
                            try {
                              const row = docSnap.data();
                              const userId = row.userId || docSnap.id;
                              
                              if (!row.totalPoints || row.totalPoints <= 0) {
                                continue;
                              }
                              
                              const p = await getDoc(doc(db, 'profiles', userId));
                              enriched.push({
                                id: userId,
                                name: p.exists() ? (p.data().artist_name || p.data().karaoke_name || 'Unknown') : 'Unknown',
                                email: p.exists() ? (p.data().email || '') : '',
                                points: row.totalPoints || 0
                              });
                            } catch (err) {
                              console.warn('Error processing leaderboard entry:', err);
                              const row = docSnap.data();
                              if (row.totalPoints && row.totalPoints > 0) {
                                enriched.push({ 
                                  id: row.userId || docSnap.id, 
                                  name: 'Unknown', 
                                  email: '', 
                                  points: row.totalPoints || 0 
                                });
                              }
                            }
                          }
                          setCompetitionLeaderboard(enriched);
                          toast({
                            title: "Leaderboard Refreshed",
                            description: `Loaded ${enriched.length} artists`,
                          });
                        } catch (error) {
                          console.error('Error refreshing leaderboard:', error);
                          toast({
                            title: "Error",
                            description: "Failed to refresh leaderboard",
                            variant: "destructive"
                          });
                        } finally {
                          setLoadingCompetition(false);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      disabled={loadingCompetition}
                      className="border-purple-600 text-purple-400 hover:bg-purple-600 hover:text-white"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingCompetition ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button
                      onClick={async () => {
                        // Confirm before resetting
                        const confirmMessage = `⚠️ WARNING: This will permanently reset ALL competition points and stats for ALL artists.\n\nThis action cannot be undone!\n\nAre you absolutely sure you want to reset the competition?`;
                        if (!window.confirm(confirmMessage)) {
                          return;
                        }
                        
                        // Double confirmation
                        const doubleConfirm = window.confirm('⚠️ FINAL CONFIRMATION: This will delete ALL competition points. Type "RESET" to confirm...\n\n(This will proceed anyway, but please confirm you understand this is permanent.)');
                        if (!doubleConfirm) {
                          return;
                        }
                        
                        setLoadingCompetition(true);
                        try {
                          // Get all competition points documents
                          const allPointsQuery = query(collection(db, 'competitionPoints'));
                          const allPointsSnapshot = await getDocs(allPointsQuery);
                          
                          if (allPointsSnapshot.empty) {
                            toast({
                              title: "No Data to Reset",
                              description: "Competition points collection is already empty",
                            });
                            setLoadingCompetition(false);
                            return;
                          }
                          
                          // Delete all documents using batch writes
                          // Firestore batch limit is 500 operations per batch
                          let deleteCount = 0;
                          const batchSize = 500;
                          let batch = writeBatch(db);
                          let operationsInBatch = 0;
                          
                          for (const docSnap of allPointsSnapshot.docs) {
                            batch.delete(docSnap.ref);
                            operationsInBatch++;
                            deleteCount++;
                            
                            // Commit batch if we reach the limit and create a new one
                            if (operationsInBatch >= batchSize) {
                              await batch.commit();
                              batch = writeBatch(db); // Create new batch
                              operationsInBatch = 0;
                            }
                          }
                          
                          // Commit any remaining deletes
                          if (operationsInBatch > 0) {
                            await batch.commit();
                          }
                          
                          // Clear the leaderboard state
                          setCompetitionLeaderboard([]);
                          
                          toast({
                            title: "Competition Reset",
                            description: `Successfully reset ${deleteCount} artist(s) competition points`,
                            variant: "default"
                          });
                          
                          console.log(`✅ Competition reset: Deleted ${deleteCount} points documents`);
                        } catch (error) {
                          console.error('Error resetting competition:', error);
                          toast({
                            title: "Error",
                            description: `Failed to reset competition: ${error.message}`,
                            variant: "destructive"
                          });
                        } finally {
                          setLoadingCompetition(false);
                        }
                      }}
                      variant="outline"
                      size="sm"
                      disabled={loadingCompetition}
                      className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {competitionLeaderboard.length > 0 ? (
                    competitionLeaderboard.map((artist, index) => (
                      <div key={artist.id} className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500">
                            <span className="text-white font-bold">{index + 1}</span>
                          </div>
                          <div>
                            <h3 className="text-white font-semibold">{artist.name}</h3>
                            <p className="text-gray-400 text-sm">{artist.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-6">
                          <div className="text-center">
                            <p className="text-2xl font-bold text-purple-400">{artist.points}</p>
                            <p className="text-xs text-gray-400">Points</p>
                          </div>
                          {index === 0 && (
                            <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                              🏆 Leader
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Radio className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                      <p className="text-gray-400">No competition data yet</p>
                      <p className="text-gray-500 text-sm">Enable the competition to start tracking points</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Communication Tab — Notifications + Support, collapsed by default so
            each can be opened on its own instead of one long scroll. */}
        {activeTab === 'communication' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <AdminCollapsibleSection
              title="Notifications"
              description="Send announcements to artists, karaoke hosts and agents"
              icon={MessageCircle}
              open={communicationSection === 'notifications'}
              onOpenChange={(open) => setCommunicationSection(open ? 'notifications' : null)}
            >
              <AdminNotificationManager />
            </AdminCollapsibleSection>

            <AdminCollapsibleSection
              title="Support"
              description="Contact form submissions and your replies"
              icon={Mail}
              open={communicationSection === 'support'}
              onOpenChange={(open) => setCommunicationSection(open ? 'support' : null)}
            >
              <ContactSubmissionsManager />
            </AdminCollapsibleSection>
          </motion.div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Referral System ON/OFF Toggle */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white">Referral System Settings</CardTitle>
                <CardDescription>
                  Referral earnings are a separate artist ledger (Refer → Payouts). Tips stay with the performing artist and are not split.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Gift className="w-6 h-6 text-pink-400" />
                    <div>
                      <h3 className="text-white font-semibold">Enable Request Rewards</h3>
                      <p className="text-gray-400 text-sm">
                        {referralSystemEnabled 
                          ? 'On a paid song: referred artist 70% · YSR 20% · referrer 10%. Track and complete referral payouts below.' 
                          : 'Referral system is disabled. No referral earnings will be calculated.'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={referralSystemEnabled}
                    onCheckedChange={async (checked) => {
                      setReferralSystemEnabled(checked);
                      try {
                        await setDoc(doc(db, 'settings', 'referralSystem'), {
                          enabled: checked,
                          updatedAt: new Date().toISOString()
                        }, { merge: true });
                        toast({
                          title: checked ? "Referral System Enabled" : "Referral System Disabled",
                          description: checked 
                            ? "Artists can now earn referral rewards" 
                            : "Referral rewards have been disabled",
                        });
                      } catch (error) {
                        console.error('Error saving referral system setting:', error);
                        toast({
                          title: "Error",
                          description: "Failed to save setting",
                          variant: "destructive"
                        });
                        // Revert on error
                        setReferralSystemEnabled(!checked);
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
            
            <AdminReferralConsole />
          </motion.div>
        )}

        {/* Gigs Tab */}
        {activeTab === 'gigs' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <AdminGigsManager />
          </motion.div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <Tabs defaultValue="live" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-gray-800">
                <TabsTrigger value="live" className="data-[state=active]:bg-red-500">
                  <Radio className="w-4 h-4 mr-2" />
                  Live Sessions
                </TabsTrigger>
                <TabsTrigger value="history" className="data-[state=active]:bg-red-500">
                  <Calendar className="w-4 h-4 mr-2" />
                  Session History
                </TabsTrigger>
              </TabsList>
              <TabsContent value="live" className="mt-6">
                <LiveSessionsView />
              </TabsContent>
              <TabsContent value="history" className="mt-6">
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center space-x-2">
                      <Calendar className="w-5 h-5" />
                      <span>All Session History</span>
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      View all completed live sessions from all artists
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <AllSessionsView />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </motion.div>
        )}

        {/* Store Tab */}
        {activeTab === 'store' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <AdminStoreManager />
          </motion.div>
        )}

        {/* QR banner short links */}
        {activeTab === 'qr-banners' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <AdminQrRedirectsPanel />
          </motion.div>
        )}

        {/* Financial Tab */}
        {activeTab === 'financial' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <AdminFinancialPanel />
          </motion.div>
        )}
      </div>

      {/* Artist Details Modal */}
      <AnimatePresence>
        {showArtistDetails && selectedArtist && (
          <ArtistDetailsModal
            artist={selectedArtist}
            liveStats={artistLiveStats[selectedArtist.id]}
            onClose={() => setShowArtistDetails(false)}
            onUpdate={handleArtistUpdate}
            onDelete={handleArtistDelete}
            onForceOffline={handleForceOffline}
            onStripeLegacyRemoved={handleStripeLegacyRemoved}
          />
        )}
      </AnimatePresence>

      {/* Artist Manager Modal */}
      <AnimatePresence>
        {showArtistManager && selectedArtist && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowArtistManager(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto mx-4 sm:mx-0"
              onClick={(e) => e.stopPropagation()}
            >
              <AdminArtistManager
                artistId={selectedArtist.id}
                onClose={() => setShowArtistManager(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Agent Manager Modal */}
      <AnimatePresence>
        {showAgentManager && selectedArtist && (
          <AgentRosterManagerModal
            agent={selectedArtist}
            onClose={() => setShowAgentManager(false)}
            onViewArtist={(artist) => {
              setSelectedArtist(artist);
              setShowAgentManager(false);
              setShowArtistDetails(true);
            }}
            onManageArtist={(artist) => {
              setSelectedArtist(artist);
              setShowAgentManager(false);
              setShowArtistManager(true);
            }}
          />
        )}
      </AnimatePresence>

    </div>
  );
};

const AgentRosterManagerModal = ({ agent, onClose, onViewArtist, onManageArtist }) => {
  const [loadingRoster, setLoadingRoster] = useState(true);
  const [roster, setRoster] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadRoster = async () => {
      if (!agent?.id) {
        setRoster([]);
        setLoadingRoster(false);
        return;
      }
      setLoadingRoster(true);
      try {
        const rosterQ = query(collection(db, 'profiles'), where('agent_id', '==', agent.id));
        const rosterSnap = await getDocs(rosterQ);
        const rows = rosterSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!cancelled) {
          setRoster(rows);
        }
      } catch (error) {
        console.error('Error loading agent roster:', error);
        if (!cancelled) {
          setRoster([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingRoster(false);
        }
      }
    };

    loadRoster();
    return () => {
      cancelled = true;
    };
  }, [agent?.id]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white">View/Manage Agent</h3>
            <p className="text-sm text-gray-400 mt-1">
              {agent.artist_name || agent.email || 'Unnamed Agent'}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="border-blue-500 text-blue-400">
                Invite code: {agent.agent_code || 'n/a'}
              </Badge>
              <Badge variant="outline" className="border-indigo-500 text-indigo-300">
                Roster: {roster.length}
              </Badge>
            </div>
          </div>
          <Button onClick={onClose} variant="outline" size="sm">
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </div>

        <div className="p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Artists signed up under this agent</h4>
          {loadingRoster ? (
            <p className="text-sm text-gray-400">Loading roster...</p>
          ) : roster.length === 0 ? (
            <p className="text-sm text-gray-400">No artists linked to this agent yet.</p>
          ) : (
            <div className="space-y-3">
              {roster.map((artist) => (
                <Card key={artist.id} className="bg-gray-900 border-gray-700">
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate">
                        {artist.artist_name || artist.karaoke_name || artist.email || artist.id}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{artist.email || 'No email'}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="border-gray-600 text-gray-300">
                          {artist.account_type || 'artist'}
                        </Badge>
                        {artist.agent_payout_preference && (
                          <Badge variant="outline" className="border-purple-600 text-purple-300">
                            Split: {artist.agent_payout_preference}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onViewArtist(artist)}
                        className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onManageArtist(artist)}
                        className="border-purple-500 text-purple-400 hover:bg-purple-500 hover:text-white"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Manage
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Admin Gigs Manager Component
const AdminGigsManager = () => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [artistMap, setArtistMap] = useState({});
  const [showAddGigDialog, setShowAddGigDialog] = useState(false);
  const [editingGig, setEditingGig] = useState(null);
  const [gigFormData, setGigFormData] = useState({
    user_id: '',
    venue_name: '',
    date: '',
    time: '',
    address: '',
    postcode: '',
    show_map: true
  });
  const [artistSearchTerm, setArtistSearchTerm] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadGigs();
    loadAllArtists();
  }, []);

  const loadAllArtists = async () => {
    if (!db) return;
    
    try {
      // Load all artist profiles for the dropdown (filter by account_type === 'artist')
      const profilesQuery = query(
        collection(db, 'profiles'),
        where('account_type', '==', 'artist')
      );
      const profilesSnapshot = await getDocs(profilesQuery);
      const allArtists = {};
      
      profilesSnapshot.docs.forEach(doc => {
        allArtists[doc.id] = { id: doc.id, ...doc.data() };
      });
      
      setArtistMap(allArtists);
      console.log(`✅ Loaded ${Object.keys(allArtists).length} artists for dropdown`);
      return allArtists;
    } catch (error) {
      console.error('Error loading artists:', error);
      // Fallback: try without filter if index is missing
      try {
        const allProfilesQuery = query(collection(db, 'profiles'));
        const allProfilesSnapshot = await getDocs(allProfilesQuery);
        const allArtists = {};
        
        allProfilesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.account_type === 'artist') {
            allArtists[doc.id] = { id: doc.id, ...data };
          }
        });
        
        setArtistMap(allArtists);
        console.log(`✅ Loaded ${Object.keys(allArtists).length} artists (fallback method)`);
        return allArtists;
      } catch (fallbackError) {
        console.error('Error loading artists (fallback):', fallbackError);
        return {};
      }
    }
  };

  const loadGigs = async () => {
    if (!db) return;
    
    setLoading(true);
    try {
      // Ensure all artists are loaded first and get the result
      const loadedArtists = await loadAllArtists();
      
      // Load all gigs
      const gigsQuery = query(
        collection(db, 'gigs'),
        orderBy('date', 'desc')
      );
      const gigsSnapshot = await getDocs(gigsQuery);
      const gigsData = gigsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get unique artist IDs from gigs
      const artistIds = [...new Set(gigsData.map(g => g.user_id))];
      
      // Merge loaded artists with any additional artists from gigs
      const currentArtistMap = { ...loadedArtists, ...artistMap };
      for (const artistId of artistIds) {
        if (!currentArtistMap[artistId]) {
          try {
            const profileDoc = await getDoc(doc(db, 'profiles', artistId));
            if (profileDoc.exists()) {
              currentArtistMap[artistId] = { id: artistId, ...profileDoc.data() };
            }
          } catch (err) {
            console.warn('Error loading artist profile:', err);
          }
        }
      }
      
      // Always update artist map to ensure it's current
      setArtistMap(currentArtistMap);
      console.log(`✅ Artist map updated with ${Object.keys(currentArtistMap).length} artists`);
      
      setGigs(gigsData);
    } catch (error) {
      console.error('Error loading gigs:', error);
      toast({
        title: "Error",
        description: "Failed to load gigs",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddGig = () => {
    setEditingGig(null);
    setArtistSearchTerm('');
    setGigFormData({
      user_id: '',
      venue_name: '',
      date: '',
      time: '',
      address: '',
      postcode: '',
      show_map: true
    });
    setShowAddGigDialog(true);
  };

  const handleEditGig = (gig) => {
    setEditingGig(gig);
    // Convert 24-hour time to 12-hour for display
    const time24 = gig.time || '';
    let time12 = '';
    let ampm = 'PM';
    if (time24) {
      const [hours, minutes] = time24.split(':');
      const hour24 = parseInt(hours, 10);
      if (hour24 === 0) {
        time12 = `12:${minutes}`;
        ampm = 'AM';
      } else if (hour24 < 12) {
        time12 = `${hour24}:${minutes}`;
        ampm = 'AM';
      } else if (hour24 === 12) {
        time12 = `12:${minutes}`;
        ampm = 'PM';
      } else {
        time12 = `${hour24 - 12}:${minutes}`;
        ampm = 'PM';
      }
    }
    
    setGigFormData({
      user_id: gig.user_id || '',
      venue_name: gig.venue_name || '',
      date: gig.date || '',
      time: time12,
      ampm: ampm,
      address: gig.address || '',
      postcode: gig.postcode || '',
      show_map: gig.show_map !== false
    });
    // Set search term to the artist's name for better UX
    const artist = artistMap[gig.user_id];
    if (artist) {
      setArtistSearchTerm(artist.artist_name || artist.karaoke_name || artist.email || '');
    }
    setShowAddGigDialog(true);
  };

  const handleDeleteGig = async (gigId) => {
    if (!window.confirm('Are you sure you want to delete this gig?')) return;
    
    try {
      await deleteDoc(doc(db, 'gigs', gigId));
      toast({
        title: "Success",
        description: "Gig deleted successfully"
      });
      loadGigs();
    } catch (error) {
      console.error('Error deleting gig:', error);
      toast({
        title: "Error",
        description: "Failed to delete gig",
        variant: "destructive"
      });
    }
  };

  const handleSaveGig = async (e) => {
    e.preventDefault();
    
    if (!gigFormData.user_id || !gigFormData.venue_name || !gigFormData.date) {
      toast({
        title: "Error",
        description: "Please fill in all required fields (Artist, Venue, Date)",
        variant: "destructive"
      });
      return;
    }

    try {
      // Convert time to 24-hour format
      const convertTimeTo24Hour = (time, ampm) => {
        if (!time || typeof time !== 'string') return '';
        const parts = time.split(':');
        if (parts.length < 2) return '';
        
        let hours = parseInt(parts[0], 10);
        let minutes = String(parts[1] || '00').trim();
        
        // Ensure minutes is a valid string
        if (!minutes || isNaN(parseInt(minutes, 10))) {
          minutes = '00';
        }
        
        // Pad minutes to 2 digits
        minutes = String(parseInt(minutes, 10)).padStart(2, '0');
        
        if (isNaN(hours)) return '';
        
        if (ampm === 'PM' && hours !== 12) {
          hours = hours + 12;
        } else if (ampm === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return `${String(hours).padStart(2, '0')}:${minutes}`;
      };

      // Convert time to 24-hour format only if time is provided
      const time24Hour = gigFormData.time 
        ? convertTimeTo24Hour(gigFormData.time, gigFormData.ampm || 'PM')
        : '';

      let gigData = {
        user_id: gigFormData.user_id,
        venue_name: gigFormData.venue_name,
        date: gigFormData.date,
        time: time24Hour,
        address: gigFormData.address || '',
        postcode: gigFormData.postcode || '',
        show_map: gigFormData.show_map !== false
      };

      // Geocode address if provided
      if (gigFormData.postcode || gigFormData.address) {
        try {
          const geocode = httpsCallable(functions, 'geocode');
          const result = await geocode({ 
            address: gigFormData.address, 
            postcode: gigFormData.postcode 
          });
          if (result.data.lat && result.data.lon) {
            gigData.lat = result.data.lat;
            gigData.lon = result.data.lon;
          }
        } catch (error) {
          console.warn('Geocoding error:', error);
          // Continue without coordinates
        }
      }

      if (editingGig) {
        await updateDoc(doc(db, 'gigs', editingGig.id), gigData);
        toast({
          title: "Success",
          description: "Gig updated successfully"
        });
      } else {
        await addDoc(collection(db, 'gigs'), {
          ...gigData,
          created_at: serverTimestamp()
        });
        toast({
          title: "Success",
          description: "Gig added successfully"
        });
      }

      setShowAddGigDialog(false);
      loadGigs();
    } catch (error) {
      console.error('Error saving gig:', error);
      toast({
        title: "Error",
        description: "Failed to save gig",
        variant: "destructive"
      });
    }
  };

  // Filter and sort gigs
  const filteredGigs = gigs
    .filter(gig => {
      if (!searchTerm) return true;
      const artist = artistMap[gig.user_id];
      const artistName = artist?.artist_name || artist?.karaoke_name || artist?.email || 'Unknown';
      const searchLower = searchTerm.toLowerCase();
      return artistName.toLowerCase().includes(searchLower) ||
             gig.venue_name?.toLowerCase().includes(searchLower) ||
             gig.address?.toLowerCase().includes(searchLower) ||
             gig.postcode?.toLowerCase().includes(searchLower);
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'date') {
        aVal = new Date(a.date || 0);
        bVal = new Date(b.date || 0);
      } else if (sortBy === 'venue_name') {
        aVal = a.venue_name || '';
        bVal = b.venue_name || '';
      } else if (sortBy === 'artist') {
        const artistA = artistMap[a.user_id];
        const artistB = artistMap[b.user_id];
        aVal = artistA?.artist_name || artistA?.karaoke_name || artistA?.email || '';
        bVal = artistB?.artist_name || artistB?.karaoke_name || artistB?.email || '';
      } else {
        aVal = a[sortBy] || 0;
        bVal = b[sortBy] || 0;
      }
      
      if (sortBy === 'date') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
        <p className="text-gray-400">Loading gigs...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center space-x-2">
                <Music className="w-5 h-5" />
                <span>Gigs Management</span>
              </CardTitle>
              <CardDescription className="text-gray-400">
                View, add, and edit performances displayed on /find-performer
              </CardDescription>
            </div>
            <Button
              onClick={handleAddGig}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Gig
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search and Sort */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <Input
              placeholder="Search by artist, venue, address, or postcode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white flex-1"
            />
            <div className="flex gap-2">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white rounded px-3 py-2"
              >
                <option value="date">Date</option>
                <option value="venue_name">Venue</option>
                <option value="artist">Artist</option>
                <option value="time">Time</option>
              </select>
              <Button
                variant="outline"
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="border-gray-600"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </div>
          </div>

          {/* Gigs List */}
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {filteredGigs.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No gigs found</p>
              </div>
            ) : (
              filteredGigs.map((gig) => {
                const artist = artistMap[gig.user_id];
                const artistName = artist?.artist_name || artist?.karaoke_name || artist?.email || 'Unknown Artist';
                const gigDate = new Date(gig.date);
                // Compare dates only (ignore time) - gig is past if its date is before today
                const now = new Date();
                const todayStart = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
                todayStart.setUTCHours(0, 0, 0, 0);
                const gigDateStart = new Date(gigDate.getUTCFullYear(), gigDate.getUTCMonth(), gigDate.getUTCDate());
                gigDateStart.setUTCHours(0, 0, 0, 0);
                const isPast = gigDateStart < todayStart;
                
                // Convert 24-hour time to 12-hour for display
                const time24 = gig.time || '';
                let timeDisplay = '';
                if (time24) {
                  const [hours, minutes] = time24.split(':');
                  const hour24 = parseInt(hours, 10);
                  if (hour24 === 0) {
                    timeDisplay = `12:${minutes} AM`;
                  } else if (hour24 < 12) {
                    timeDisplay = `${hour24}:${minutes} AM`;
                  } else if (hour24 === 12) {
                    timeDisplay = `12:${minutes} PM`;
                  } else {
                    timeDisplay = `${hour24 - 12}:${minutes} PM`;
                  }
                }

                return (
                  <Card
                    key={gig.id}
                    className={`bg-gray-700 border-gray-600 hover:bg-gray-600 transition-colors ${isPast ? 'opacity-60' : ''}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Music className="w-4 h-4 text-blue-400" />
                            <span className="font-semibold text-white text-lg">{gig.venue_name}</span>
                            {isPast && (
                              <Badge variant="outline" className="border-gray-500 text-gray-400 text-xs">
                                Past
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-3">
                            <div>
                              <p className="text-gray-400">Artist</p>
                              <p className="text-white font-medium">{artistName}</p>
                            </div>
                            <div>
                              <p className="text-gray-400">Date</p>
                              <p className="text-white font-medium">
                                {formatUkDate(gigDate)}
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-400">Time</p>
                              <p className="text-white font-medium">{timeDisplay || 'Not set'}</p>
                            </div>
                          </div>

                          {(gig.address || gig.postcode) && (
                            <div className="mt-2 flex items-center space-x-2 text-sm text-gray-400">
                              <MapPin className="w-4 h-4" />
                              <span>
                                {gig.address && `${gig.address}, `}
                                {gig.postcode}
                              </span>
                            </div>
                          )}

                          {gig.lat && gig.lon && (
                            <div className="mt-2 text-xs text-gray-500">
                              Coordinates: {gig.lat.toFixed(4)}, {gig.lon.toFixed(4)}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditGig(gig)}
                            className="border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteGig(gig.id)}
                            className="border-red-500 text-red-400 hover:bg-red-500 hover:text-white"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Gig Dialog */}
      <Dialog open={showAddGigDialog} onOpenChange={setShowAddGigDialog}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingGig ? 'Edit Gig' : 'Add New Gig'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveGig} className="space-y-4">
            {/* Artist Selection with Search */}
            <div className="space-y-2">
              <Label htmlFor="artist_search" className="text-gray-200">Artist *</Label>
              <Input
                id="artist_search"
                type="text"
                placeholder="Search for an artist..."
                value={artistSearchTerm}
                onChange={(e) => setArtistSearchTerm(e.target.value)}
                className="bg-gray-700 border-gray-600 text-white mb-2"
              />
              <select
                id="user_id"
                value={gigFormData.user_id}
                onChange={(e) => {
                  setGigFormData(prev => ({ ...prev, user_id: e.target.value }));
                  // Update search term when selection changes
                  const selectedArtist = artistMap[e.target.value];
                  if (selectedArtist) {
                    setArtistSearchTerm(selectedArtist.artist_name || selectedArtist.karaoke_name || selectedArtist.email || '');
                  }
                }}
                className="w-full bg-gray-700 border-gray-600 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="">Select an artist...</option>
                {Object.entries(artistMap)
                  .filter(([artistId, artist]) => {
                    if (!artistSearchTerm) return true;
                    const searchLower = artistSearchTerm.toLowerCase();
                    const artistName = (artist.artist_name || artist.karaoke_name || artist.email || artistId || '').toLowerCase();
                    return artistName.includes(searchLower);
                  })
                  .map(([artistId, artist]) => (
                    <option key={artistId} value={artistId}>
                      {artist.artist_name || artist.karaoke_name || artist.email || artistId}
                    </option>
                  ))}
              </select>
              {Object.keys(artistMap).length === 0 && (
                <p className="text-sm text-gray-400">Loading artists...</p>
              )}
              {Object.keys(artistMap).length > 0 && artistSearchTerm && (
                <p className="text-xs text-gray-500">
                  {Object.entries(artistMap).filter(([artistId, artist]) => {
                    if (!artistSearchTerm) return true;
                    const searchLower = artistSearchTerm.toLowerCase();
                    const artistName = (artist.artist_name || artist.karaoke_name || artist.email || artistId || '').toLowerCase();
                    return artistName.includes(searchLower);
                  }).length} artist(s) found
                </p>
              )}
            </div>

            {/* Venue Name */}
            <div className="space-y-2">
              <Label htmlFor="venue_name" className="text-gray-200">Venue Name *</Label>
              <Input
                id="venue_name"
                value={gigFormData.venue_name}
                onChange={(e) => setGigFormData(prev => ({ ...prev, venue_name: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date" className="text-gray-200">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={gigFormData.date}
                  onChange={(e) => setGigFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time" className="text-gray-200">Time</Label>
                <div className="flex gap-2">
                  <Input
                    id="time"
                    type="time"
                    value={gigFormData.time}
                    onChange={(e) => setGigFormData(prev => ({ ...prev, time: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                  />
                  <select
                    value={gigFormData.ampm || 'PM'}
                    onChange={(e) => setGigFormData(prev => ({ ...prev, ampm: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white rounded px-3 py-2"
                  >
                    <option value="AM">AM</option>
                    <option value="PM">PM</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Address and Postcode */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="address" className="text-gray-200">Address</Label>
                <Input
                  id="address"
                  value={gigFormData.address}
                  onChange={(e) => setGigFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Street address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="postcode" className="text-gray-200">Postcode</Label>
                <Input
                  id="postcode"
                  value={gigFormData.postcode}
                  onChange={(e) => setGigFormData(prev => ({ ...prev, postcode: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="Postcode"
                />
              </div>
            </div>


            {/* Show Map */}
            <div className="flex items-center space-x-2">
              <Switch
                id="show_map"
                checked={gigFormData.show_map}
                onCheckedChange={(checked) => setGigFormData(prev => ({ ...prev, show_map: checked }))}
              />
              <Label htmlFor="show_map" className="text-gray-200">Show on map</Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddGigDialog(false)}
                className="border-gray-600"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700"
              >
                {editingGig ? 'Update Gig' : 'Add Gig'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default AdminConsolePage;
